import { uniq } from 'es-toolkit';

import { assertExhaustiveSwitch } from '../../utilities';
import { BuildingCode, DeflectionLoading, DesignStandard, LayoutModel } from '..';

export interface AnalysisResponse {
  readonly analysisSet: AnalysisSet;
  readonly analysisHistoryRecord: AnalysisHistoryRecord;
  readonly componentDesignTimestamps: ComponentDesignTimestamps;
  readonly excavation: Excavation | null;
  readonly trussDesignUsage: UsageData;
}

export interface Migratable {
  readonly version: number;
}

export interface BlobStorageStorable extends Migratable {
  readonly blobName: string;
}

export interface AnalysisResult extends BlobStorageStorable {
  readonly guid: string;
  readonly analysisSetGuid: string;
  readonly loadCaseGuid: string;
  readonly capacity: number;
  readonly analogLinkMaxVerticalDeflections: readonly AnalogLinkMaxVerticalDeflection[];
  readonly checkResults: readonly CheckResult[];
  readonly analogJointResults: readonly AnalogJointResult[];
  readonly linkEndResults: readonly AnalogLinkEndResult[];
  readonly analogLinkResults: readonly ResultCurve[];
  readonly platingForceResults: readonly PlatingForceResult[];
  readonly reducedWindLoadsForCC: boolean;
}

export interface ResultJointGuid {
  readonly analogJointGuid: string;
}

export interface ResultLinkGuid {
  readonly analogLinkGuid: string;
}

export type PlatingForceType = 'Merged' | 'Compression' | 'Tension' | 'Exception';

export interface PlatingForceResult extends ResultJointGuid {
  readonly guid: string;
  readonly analogJointGuid: string;
  readonly analysisResultGuid: string;
  readonly memberName: string;
  readonly platingForceType: PlatingForceType;
  readonly xForce: number;
  readonly yForce: number;
  readonly moment: number;
}

export interface ResultCurve extends ResultLinkGuid {
  readonly guid: string;
  readonly analysisResultGuid: string;
  readonly analogLinkGuid: string;
  readonly curveType: string;
  readonly coefficients: number[];
}

export interface AnalogLinkEndResult extends ResultJointGuid, ResultLinkGuid {
  readonly guid: string;
  readonly analogLinkGuid: string;
  readonly analogJointGuid: string;
  readonly analysisResultGuid: string;
  readonly axialForce: number;
  readonly transverseForce: number;
  readonly xForce: number;
  readonly yForce: number;
  readonly moment: number;
  readonly angularDeflection: number;
}

export interface AnalogJointResult extends ResultJointGuid {
  readonly guid: string;
  readonly analogJointGuid: string;
  readonly analysisResultGuid: string;
  readonly xDeflection: number;
  readonly yDeflection: number;
  readonly angularDeflection: number;
  readonly xForce: number;
  readonly yForce: number;
  readonly moment: number;
}

export interface AnalogLinkMaxVerticalDeflection {
  readonly analogLinkGuid: string;
  readonly deflection: number;
}

export type CheckCategory = 'Lumber' | 'Plate' | 'None';

export interface ObjectChecked {
  readonly guid: Guid;
  readonly type: string;
  readonly name: string;
}

export interface CheckResult {
  readonly guid: string;
  readonly name: string;
  // from lowest child to highest child.
  readonly objects: readonly ObjectChecked[];
  readonly checkCategory: CheckCategory;
  readonly capacity: number;
  readonly tpiSection: string;
  readonly variables: readonly Variable[] | null;
}

export interface Variable {
  readonly name: string;
  readonly value: string | null;
  readonly children?: readonly Variable[];
}

export type CheckFilter = 'AllChecks' | 'LumberChecks' | 'MemberChecks' | 'PlateChecks';

export interface AnalysisSet extends BlobStorageStorable {
  readonly guid: string;
  readonly timeStamp: string;
  readonly analysisReportGuid: string;
  readonly checkFilter: CheckFilter;
  readonly componentDesign: ComponentDesign;
  readonly analogModelGuid: string;
  readonly analogModel: AnalogModel;
  readonly capacity: number;
  // This is null for analysis sets for which analysis results have not been saved or on which
  // https://github.com/ParagonTruss/ParagonCore/blob/d9c29787809e0ba345ca5e69c28cc40355f38c4f/servers/apps/Affenpinscher/Migrations/PopulateAnalysisResultGuidsByLoadCase.cs
  // has not been run.
  readonly analysisResultGuidsByLoadCase: { readonly [loadCaseGuid: string]: string } | null;
  // This can be removed once
  // https://github.com/ParagonTruss/ParagonCore/blob/d9c29787809e0ba345ca5e69c28cc40355f38c4f/servers/apps/Affenpinscher/Migrations/PopulateAnalysisResultGuidsByLoadCase.cs
  // has been run. It is null for analysis sets for which analysis results have not been saved.
  readonly analysisResultGuids: readonly string[] | null;
  readonly analysisSettings: AnalysisSettings;
  readonly loadSettings: LoadSettings;
  readonly csi: CsiSummary;
  readonly jsi: string;
  readonly memberStressSummaries: readonly MemberStressSummary[];
  readonly maximumBearingReactions: readonly MaximumBearingReaction[];
  readonly bearingsByJoint: { readonly [jointGuid: string]: string } | null;
  readonly maxDeflectionTable: MaxDeflectionTable | null;
  readonly loadCaseCapacities: { readonly [loadCaseGuid: string]: number } | null;
  readonly objectCapacities: {
    readonly [objectGuid: string]: { readonly objectChecked: ObjectChecked; readonly capacity: number };
  } | null;
  readonly objectCheckFailureSummaries: readonly ObjectCheckFailureSummary[] | null;
  readonly capacityRatiosAlongMembers: { readonly [memberGuid: string]: readonly number[] } | null;
  readonly jsiByJoint: ReadonlyArray<{ readonly joint: string; readonly jsi: number }> | null;
  readonly splicePlateRecommendations:
    | readonly {
        readonly joint: string;
        readonly distanceToEdge: number | null;
        readonly length: number;
        readonly width: number;
      }[]
    | null;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
  readonly bracing: {
    readonly [memberGuid: string]: readonly { point: Point2D; direction: Direction2D }[];
  };
  readonly calculatedSlopedTopChordPurlinSpacing: number | null;
  readonly calculatedBottomChordPurlinSpacing: number | null;
  readonly calculatedFlatTopChordPurlinSpacing: number | null;
  readonly isGypsumSufficientForBottomChords: boolean;
  readonly calculatedCamber: number;
}

export interface ObjectCheckFailureSummary {
  readonly objectChecked: ObjectChecked;
  readonly checkFailureSummary: string;
  readonly capacityRatio: number;
}

export interface LoadSet extends BlobStorageStorable {
  readonly guid: string;
  readonly timestamp: string | null;
  readonly loadCases: readonly LoadCase[];
  readonly atticRoomLoadingRecord: unknown;
  readonly girderConnectionLoadSummaries: unknown;
  readonly usesCommitteeInterpretationForPersonLoads: boolean;
  readonly unreportedCanaryToxins: readonly string[] | null;
}

export interface CsiSummary {
  readonly topChord: CsiResult;
  readonly bottomChord: CsiResult;
  readonly web: CsiResult;
}

export interface CsiResult {
  readonly memberType: string;
  readonly link: string;
  readonly csi: number;
  readonly loadCase: number;
}

export interface MaximumBearingReaction {
  readonly jointName: string;
  readonly upForce: BearingReaction | null;
  readonly downForce: BearingReaction | null;
  readonly horizontalForce: BearingReaction;
  readonly moment: never;
  readonly actualBearingWidth: number | null;
  readonly requiredBearingWidth: number | null;
}
export interface BearingReaction {
  readonly loadCaseIndex: number;
  readonly force: number;
}

export interface MemberStressSummary {
  readonly csi: ValueWithLoadCaseIndex;
  readonly ssi: ValueWithLoadCaseIndex;
  readonly linkGuid: string;
  readonly adjustedIEndMoment: ValueWithLoadCaseIndex;
  readonly adjustedJEndMoment: ValueWithLoadCaseIndex;
  readonly adjustedMidPanelMoment: ValueWithLoadCaseIndex;
  readonly purlinSpacing: number;
  readonly compression: ValueWithLoadCaseIndex | null;
  readonly tension: ValueWithLoadCaseIndex | null;
  readonly midPanelAxial: ValueWithLoadCaseIndex;
  readonly shear: ValueWithLoadCaseIndex;
}

export interface ValueWithLoadCaseIndex {
  readonly value: number;
  readonly loadCaseIndex: number;
}

export interface MemberGripAnalysis {
  readonly memberGuid: string;
  readonly capacityRatio: number;
  readonly geometry: readonly Point2D[] | null;
}

export interface PlateAnalysis {
  readonly memberAnalysis: readonly MemberGripAnalysis[];
}

export interface AnalysisSettings {
  readonly designStandard: DesignStandard;
  readonly roofDeflectionLimits: RoofDeflectionSettings;
  readonly floorDeflectionLimits: FloorDeflectionSettings;
  readonly temperature: string;
  readonly chordBracing: ChordBracing;
  readonly useWetServiceFactor: boolean;
  readonly useRepetitiveMemberIncrease: boolean;
  readonly useBendingModificationFactor: boolean;
  readonly incisedFactor: number;
  readonly plateCompressiveForceReduction: number;
  readonly creepFactor: number;
  readonly multiPlyConnectionType: string;
  readonly manualCamber: number;
  readonly useManualCamber: boolean;
  readonly platingJsiLimit: number;
  readonly maxPlateIntervalAlongStacks: number | null;
  readonly assumeCompositeActionForStackedMembers: boolean;
  readonly requiredLateralBite: number;
  /** A setting of null will be mapped to a default boolean value based on the applicable TPI 1 version. */
  readonly pinEndVerticalJointsRoof: boolean | null;
  readonly plateRibbonBlocks: boolean;
  readonly fabricationTolerancePercentageFrontBackPlates: number;
  readonly fabricationTolerancePercentageTopBottomPlates: number;
  readonly heelPlateMinWidth: number;
  readonly heelPlateMinLength: number;
  readonly peakPlateMinWidth: number;
  readonly peakPlateMinLength: number;
  readonly tryOffCenterPlateShifts: boolean;
}

interface DeflectionSettings {
  readonly liveTopChordSpanToDeflectionRatio: number;
  readonly liveTopChordAbsoluteDeflection: number;
  readonly liveBottomChordSpanToDeflectionRatio: number;
  readonly liveBottomChordAbsoluteDeflection: number;
  readonly liveTrussSpanToDeflectionRatio: number;
  readonly liveTrussAbsoluteDeflection: number;
  readonly liveHorizontal: number;
  readonly liveCantileverSpanToDeflectionRatio: number;
  readonly liveCantileverAbsoluteDeflection: number;
  readonly liveOverhangSpanToDeflectionRatio: number;
  readonly liveOverhangAbsoluteDeflection: number;
  readonly liveWebSpanToDeflectionRatio: number;
  readonly liveWebAbsoluteDeflection: number;
  readonly totalTopChordSpanToDeflectionRatio: number;
  readonly totalTopChordAbsoluteDeflection: number;
  readonly totalBottomChordSpanToDeflectionRatio: number;
  readonly totalBottomChordAbsoluteDeflection: number;
  readonly totalTrussSpanToDeflectionRatio: number;
  readonly totalTrussAbsoluteDeflection: number;
  readonly totalHorizontal: number;
  readonly totalCantileverSpanToDeflectionRatio: number;
  readonly totalCantileverAbsoluteDeflection: number;
  readonly totalOverhangSpanToDeflectionRatio: number;
  readonly totalOverhangAbsoluteDeflection: number;
  readonly totalWebSpanToDeflectionRatio: number;
  readonly totalWebAbsoluteDeflection: number;
}

export type RoofDeflectionSettings = DeflectionSettings & {
  readonly liveWallSpanToDeflectionRatio: number;
  readonly liveWallAbsoluteDeflection: number;
  /** Optional override to using `liveTrussSpanToDeflectionRatio` field of floor truss deflection settings  */
  readonly liveAtticRoomFloorSpanToDeflectionRatio: number | null;
  /** Optional override to using `liveTrussAbsoluteDeflection` field of floor truss deflection settings */
  readonly liveAtticRoomFloorAbsoluteDeflection: number | null;
  readonly totalWallSpanToDeflectionRatio: number;
  readonly totalWallAbsoluteDeflection: number;
};

export type FloorDeflectionSettings = DeflectionSettings;

export interface TransferComponentDesignFullyLoaded extends ComponentDesign {
  readonly loadSettings: LoadSettings;
  readonly analysisSettings: AnalysisSettings;
  readonly materialSettings: MaterialSettings;
  readonly loadSet: LoadSet | null;
}

export interface ComponentDesign extends Migratable {
  readonly guid: string;
  readonly component: Component;
  readonly jobId: number;
  readonly organization: string | null;
  readonly dimensionSetGuid: string;
  readonly additionalLoads: AdditionalLoads | null;
  readonly analogModel: AnalogModel | null;
  readonly profile: Profile | null;
  readonly loadSetGuid: string | null;
  readonly materialSelection: MaterialSelection;
  readonly analogLocked: boolean;
  readonly hangersLocked: boolean;
  readonly lockedHangers: string[];
  readonly platingLocked: boolean;
  readonly lockedPlatePairs: string[];
  readonly loadingLocked: boolean;
  readonly membersLocked: boolean;
  readonly lockedMembers: string[];
  readonly userDistanceDimensions: readonly UserDistanceDimension[];
}

interface ComponentBase {
  readonly name: string;
  readonly guid: string;
  readonly componentType: ComponentType;
  readonly definitionType: ComponentDefinitionType;
  readonly members: readonly Member[];
  readonly gussets: readonly Gusset[];
  readonly platePairs: readonly PlatePair[];
  readonly hangers: readonly Hanger[];
  readonly bearings: readonly Bearing[];
  readonly referenceLines: readonly CalculatedLine[] | null;
  readonly chordSplices: readonly Splice[];
  readonly weight: number;
  readonly span: number;

  readonly verticalZeroGuid: string | null;
  readonly verticalMaxGuid: string | null;
  readonly horizontalZeroGuid: string | null;
  readonly horizontalMaxGuid: string | null;
  readonly leftCutBackTopChordGuid: string | null;
  readonly leftCutBackBottomChordGuid: string | null;
  readonly rightCutBackTopChordGuid: string | null;
  readonly rightCutBackBottomChordGuid: string | null;
  readonly leftInnerMostEndConditionVerticalGuid: string | null;
  readonly rightInnerMostEndConditionVerticalGuid: string | null;
  readonly overhangLeftGuid: string | null;
  readonly overhangRightGuid: string | null;
  readonly seatCutLeftGuid: string | null;
  readonly seatCutRightGuid: string | null;
  readonly overhangOneGuid: string | null;
  readonly overhangTwoGuid: string | null;
}

export interface Splice {
  readonly name: string;
  readonly guid: string;
  readonly neighbor1Guid: string;
  readonly neighbor2Guid: string;
  readonly centerPointX: number;
  readonly centerPointY: number;
  readonly centerPointZ: number;
}

export type Component = ProfileTruss | FeaturePatternTruss | Wall;
export type ComponentType = TrussType | 'Wall';
export const TRUSS_DEFINITION_TYPES = ['Profile', 'FeaturePattern'] as const;
export type TrussDefinitionType = (typeof TRUSS_DEFINITION_TYPES)[number];
export type ComponentDefinitionType = TrussDefinitionType | 'Wall';
export type ComponentTemplateType = 'default' | 'G2T-TC-TC' | 'G2T-BC-BC' | 'G2T-BC-TC' | 'G2T-TC-BC' | 'PFT' | 'PFT-J';
export const TRUSS_COMPONENT_TYPES = ['RoofTruss', 'FloorTruss'] as const;
export type TrussType = (typeof TRUSS_COMPONENT_TYPES)[number];

export const isTruss = (component: Component | null | undefined): component is Truss => {
  if (component == null) return false;
  return component.componentType === 'RoofTruss' || component.componentType === 'FloorTruss';
};

export const isFeaturePatternTruss = (component: Component | null | undefined): component is FeaturePatternTruss =>
  isTruss(component) && component.definitionType === 'FeaturePattern';

export const isProfileTruss = (component: Component | null | undefined): component is ProfileTruss =>
  isTruss(component) && component.definitionType === 'Profile';

export const VALID_DEFINITION_TYPES: Record<TrussType, readonly TrussDefinitionType[]> = {
  RoofTruss: ['Profile', 'FeaturePattern'],
  FloorTruss: ['FeaturePattern'],
};

export function getDefinitionTypeForComponentType(
  componentType: TrussType,
  lastRoofTrussDefinitionType: TrussDefinitionType,
): TrussDefinitionType {
  const validDefinitionTypes = VALID_DEFINITION_TYPES[componentType];
  return validDefinitionTypes.includes(lastRoofTrussDefinitionType)
    ? lastRoofTrussDefinitionType
    : validDefinitionTypes[0];
}

export function getComponentTypeForDefinitionType(
  definitionType: TrussDefinitionType,
  lastComponentType: TrussType,
): TrussType {
  if (VALID_DEFINITION_TYPES[lastComponentType].includes(definitionType)) return lastComponentType;

  const componentType = TRUSS_COMPONENT_TYPES.find((type) => VALID_DEFINITION_TYPES[type].includes(definitionType));
  if (componentType == null) throw new Error('Invalid component type <=> definition type relationship');
  return componentType;
}

interface TrussBase extends ComponentBase {
  readonly componentType: TrussType;
  readonly numberOfPlies: number;
  readonly spacing: number;
  readonly gableSettings: GableSettings;
  readonly thickness: number;
  readonly braceMembers: readonly Member[];
}
export type Truss = ProfileTruss | FeaturePatternTruss;

export interface Heel {
  readonly height: number;
}

export interface Overhang {
  readonly distance: number;
}

export interface ProfileTruss extends TrussBase {
  readonly definitionType: 'Profile';

  readonly leftOverhang: Overhang;
  readonly rightOverhang: Overhang;

  readonly rightHeel: Heel;
  readonly leftHeel: Heel;
}

export interface FeaturePatternTruss extends TrussBase {
  readonly definitionType: 'FeaturePattern';
  readonly leftEndCondition: EndCondition;
  readonly rightEndCondition: EndCondition;
  readonly zones: readonly Zone[];

  readonly settings: FeaturePatternTrussSettings | null;
  readonly inferredSettings: FeaturePatternTrussSettings | null;
}

export interface EndCondition {
  readonly guid: string;
  readonly name: string;
  readonly isTopChordBearing: boolean;
}

export interface Zone {
  readonly guid: string;
  readonly name: string;
  readonly chases: readonly Chase[];
  readonly runs: readonly Run[];
}

export interface Chase {
  readonly name: string;
  readonly guid: string;
  readonly span: number;
  readonly centerInX: number;
}

export interface Run {
  readonly name: string;
  readonly guid: string;
  readonly webbingStart: WebbingStart;
  readonly webbingEnd: WebbingStart;
  readonly span: number;
}

export interface Wall extends ComponentBase {
  readonly componentType: 'Wall';
}

export interface SheathingType {
  readonly guid: string;
  readonly name: string;
  readonly thickness: number;
  readonly width: number;
  readonly height: number;
  readonly structural: boolean;
}

export interface JackGirderConnection {
  readonly guid: string;

  readonly locked: boolean;

  readonly userCreated: boolean;

  readonly jackGuid: string;
  readonly girderGuid: string;

  readonly jackBearingGuid: string;
  readonly memberType: MemberType;
  readonly chordToUseWhenMultipleChoices: 'Outer' | 'Inner';
  readonly xLocation: number;
  readonly minimumAngleBetweenGirderAndJack: number;
  readonly skewFromLeft: boolean | null;
  readonly girderFace: HangerLocationFace | null;
  readonly hangerGuid: string | null;
  readonly hangerCarriedItemConfigurationGuid: string | null;
  readonly numberOfJacks: number;
  readonly spacingOfJacks: number;
}

export interface MovingLoadSettings {
  readonly guid: string;
  readonly loadDistribution: Distribution;
  readonly loadMagnitude: number;
  readonly magnitudeUnit: MagnitudeUnit;
  readonly distributedLoadLength: number;
  readonly startLocationX: number;
  readonly endLocationX: number;
  readonly distanceBetweenLoads: number;
  readonly usePanelPoints: boolean;
  readonly loadType: LoadType;
  readonly alongMemberType: MemberType;
  readonly name: string | null;
}

export interface AdditionalLoads {
  readonly manualLoadCases: readonly LoadCase[];
  readonly manualLoadSettings: readonly ManualLoadSettings[];
  readonly parapetManualLoadSettings: readonly ParapetManualLoadSettings[];
  readonly movingLoadSettings: readonly MovingLoadSettings[];
}

export interface ParapetManualLoadSettings {
  readonly guid: string;
  readonly loadCaseALoads: readonly ManualLoadDefinition[];
  readonly loadCaseBLoads: readonly ManualLoadDefinition[];
  readonly name: string | null;
}

export const roofShapes = ['Gable', 'Hip', 'Stepped', 'MultispanGable', 'Monoslope', 'Sawtooth'];
export type RoofShape = (typeof roofShapes)[number];

export interface LoadSettings {
  readonly buildingCode: BuildingCode;
  readonly roofTopChordLiveLoad: number;
  readonly roofTopChordDeadLoad: number;
  readonly overhangDeadLoad: number;
  readonly roofBottomChordLiveLoad: number;
  readonly roofBottomChordDeadLoad: number;
  readonly floorTopChordLiveLoad: number;
  readonly floorTopChordDeadLoad: number;
  readonly floorBottomChordLiveLoad: number;
  readonly floorBottomChordDeadLoad: number;
  readonly applyBottomChordLiveLoadNonConcurrently: boolean;
  readonly considerUnbalancedRoofLiveLoad: boolean;
  readonly considerUnbalancedFloorLiveLoad: boolean;
  readonly terrainExposureCategory: TerrainExposureCategory;
  readonly riskCategory: RiskCategory;
  readonly useWind: boolean;
  readonly windSpeed: number;
  readonly enclosureClassification: EnclosureClassification;
  readonly meanRoofHeight: number;
  readonly buildingWidth: number;
  readonly buildingLength: number;
  readonly horizontalDistanceToWindwardEdge: number;
  readonly windTopographicFactor: number;
  readonly groundElevationFactor: number;
  readonly windDirectionalityFactor: number;
  readonly windGustEffectFactor: number;
  readonly maxTopChordDeadLoadForUpliftOverride: number | null;
  readonly maxBottomChordDeadLoadForUpliftOverride: number | null;
  readonly windMwfrsZone: string;
  readonly windCcZone: string;
  readonly isRightCantileverExposed: boolean;
  readonly isLeftCantileverExposed: boolean;
  readonly isRightPorchExposed: boolean;
  readonly isLeftPorchExposed: boolean;
  readonly isRightEndVerticalExposed: boolean;
  readonly isLeftEndVerticalExposed: boolean;
  readonly windDesignMethod: WindDesignMethod;
  readonly roofShape: RoofShape;
  readonly useSlopedTopChordAngleForFlatTopChords: boolean;
  readonly angleForFlatTopChords: number;
  readonly useTornado: boolean;
  readonly tornadoSpeed: number;
  readonly tornadoGustEffectFactor: number;
  readonly reclassifyEnclosedAsPartiallyEnclosedForTornadoLoads: boolean;
  readonly essentialFacilityOrNecessaryThereto: boolean;
  readonly useSnow: boolean;
  readonly activeSnowLoad: ActiveSnowLoad;
  readonly snowLoad: number;
  readonly exposure: Exposure;
  readonly thermalCondition: ThermalCondition;
  readonly rValue: number;
  readonly isVentilated: boolean;
  readonly manualThermalFactor: number | null;
  readonly applySlopeReductionFactors: boolean;
  readonly slipperyRoof: boolean;
  readonly considerUnbalancedSnow: boolean;
  readonly snowDesignMethod: string;
  readonly isSawtoothRoof: boolean;
  readonly includeDriftLoads: boolean;
  readonly snowFetchDistancePerpendicularToTruss: number;
  readonly winterWindParameter: number;
  readonly considerAtticLoad: boolean;
  readonly atticRoomHeight: number;
  readonly atticRoomWidth: number;
  readonly atticFloorLiveLoad: number;
  readonly atticFloorDeadLoad: number;
  readonly atticWallDeadLoad: number;
  readonly atticCeilingDeadLoad: number;
  readonly leftEaveToRidgeDistance: number;
  readonly rightEaveToRidgeDistance: number;
  readonly considerDragLoad: boolean;
  readonly totalDragLoad: number;
  readonly dragLoadUnit: 'Pounds' | 'Plf';
  readonly dragLoadRestraint: number;
  readonly dragLoadTakenOutOnTopChord: boolean;
  readonly dragLoadTakenOutLeftSide: boolean;
  readonly bcRestraintLocations: readonly BCRestraintSegments[] | null;
  readonly considerSprinklerLoad: boolean;
  readonly sprinklerLiveLoad: number;
  readonly applySprinklerAlongTC: boolean;
  readonly applySprinklerAlongBC: boolean;
  readonly considerMovingOfficeLoad: boolean;
  readonly movingOfficeLoad: number;
  readonly movingOfficeLoadLength: number;
  readonly oneOrTwoFamilyDwelling: boolean | null;
  readonly useFullStorageLoad: boolean;
  readonly applyReducedStorageLoadNonConcurrently: boolean;
  readonly baseLoadingOffGeometry: boolean;
  readonly tributaryArea: number;
  readonly highVelocityHurricaneZone: boolean;
  readonly anyDrainageObstructions: boolean;
  readonly occupancyUse: string;
  readonly occupancyUseReductionFactor: number;
  readonly otherUse: string;
  readonly otherUseReductionFactor: number;
  readonly otherUseLoad: number;
  readonly addHalfWeightOfTrussToRoofTCDeadLoad: boolean;
  readonly addHalfWeightOfTrussToRoofBCDeadLoad: boolean;
  readonly addHalfWeightOfTrussToFloorTCDeadLoad: boolean;
  readonly addHalfWeightOfTrussToFloorBCDeadLoad: boolean;
  readonly considerPersonLoads: boolean;
  readonly useCommitteeInterpretationForPersonLoads: boolean;
  readonly deadDurationFactor: number;
  readonly floorLiveDurationFactor: number;
  readonly roofLiveDurationFactor: number;
  readonly windDurationFactor: number;
  readonly tornadoDurationFactor: number;
  readonly snowDurationFactor: number;
  readonly rainDurationFactor: number;
  readonly dragDurationFactor: number;
  readonly personDurationFactor: number;
}

export type TerrainExposureCategory = 'B_SuburbanAreas' | 'C_ScatteredObstructions' | 'D_FlatOpenTerrain';

export type ActiveSnowLoad = 'UltimateGround' | 'AsdGround' | 'Roof';

export type Exposure = 'FullyExposed' | 'PartiallyExposed' | 'Sheltered';

export type ThermalCondition = 'Other' | 'ColdVented' | 'Unheated' | 'FreezerBuilding' | 'Greenhouse';

export type RiskCategory = 'I' | 'II' | 'III' | 'IV';

export type WindDesignMethod =
  | 'WindAsceCc'
  | 'WindAsceMwfrsEnvelope'
  | 'WindAsceMwfrsDirectional'
  | 'WindAsceHybridEnvelope'
  | 'WindAsceHybridDirectional';

export type EnclosureClassification = 'Enclosed' | 'PartiallyEnclosed' | 'PartiallyOpen' | 'Open';

export const commonMaterialSettingsProperties = [
  'topChordLumber',
  'bottomChordLumber',
  'webLumber',
  'bearingBlockLumber',
  'endVerticalLumber',
  'gableStudLumber',
  'sliderLumber',
] as const;

export const uniqueProfileMaterialSettingsProperties = ['wedgeLumber'] as const;
export const uniqueFeaturePatternMaterialSettingsProperties = ['ribbonBlockLumber'] as const;

export const profileMaterialSettingsProperties = [
  ...commonMaterialSettingsProperties,
  ...uniqueProfileMaterialSettingsProperties,
] as const;

export const featurePatternMaterialSettingsProperties = [
  ...commonMaterialSettingsProperties,
  ...uniqueFeaturePatternMaterialSettingsProperties,
] as const;

export const materialSettingsLumberProperties = uniq([
  ...profileMaterialSettingsProperties,
  ...featurePatternMaterialSettingsProperties,
]);

export interface MaterialSettings {
  readonly topChordLumber: Lumber;
  readonly bottomChordLumber: Lumber;
  readonly webLumber: Lumber;
  readonly bearingBlockLumber: Lumber;
  readonly endVerticalLumber: Lumber;
  readonly gableStudLumber: Lumber;
  readonly sliderLumber: Lumber;
  readonly wedgeLumber: Lumber;
  readonly ribbonBlockLumber: Lumber;
}

export interface MaterialSettingsPair {
  readonly profileMaterialSettings: MaterialSettings | null;
  readonly featurePatternMaterialSettings: MaterialSettings | null;
}

export interface BasicProfile {
  readonly profileLines: readonly ProfileLine[];
  readonly profileJoints: readonly ProfileJoint[];
  readonly topChordMaxPanelLength: number;
  readonly bottomChordMaxPanelLength: number;
}

export interface Profile extends BasicProfile {
  readonly guid: string;
  readonly leftOverhang: OverhangSettings;
  readonly rightOverhang: OverhangSettings;
  readonly leftHeelSettings: HeelSettings;
  readonly rightHeelSettings: HeelSettings;
  readonly leftBevelCut: BevelCut | null;
  readonly rightBevelCut: BevelCut | null;
  readonly jackSettings: JackSettings | null;
  readonly piggybackSettings: PiggybackSettings | null;
  readonly atticSettings: AtticSettings | null;
  readonly name: string;
  readonly continuouslyBearing: boolean;
  readonly generatableBearings: readonly GeneratableBearing[];
  readonly spacing: number;
  readonly plies: number;
}

export interface WebJointSegment {
  readonly guid: string;
  readonly baseJointGuid: string;
  readonly endJointGuid: string;
  readonly numberOfFloatingJoints: number | null;
  readonly floatingJoints: readonly WebJoint[];
}

export interface WebJoint {
  readonly guid: string;
  readonly x: number;
}

export type ProfileLineType = 'TopChord' | 'BottomChord' | 'SeatCut' | 'Heel';

export interface ProfileLine {
  readonly guid: string;
  readonly profileLineType: ProfileLineType;
  readonly fixedJoints: readonly WebJoint[];
  readonly featureJoints: readonly WebJoint[];
  readonly webJointSegments: readonly WebJointSegment[];
  readonly baseJointGuid: string;
  readonly endJointGuid: string;
}

export interface ProfileJoint {
  readonly guid: string;
  readonly location: Point2D;
}

export const OVERHANG_CUT_TYPES = ['Square', 'Plumb', 'Horizontal', 'Angle'] as const;
export type OverhangCutType = (typeof OVERHANG_CUT_TYPES)[number];

export interface OverhangSettings {
  readonly overhangCutType: OverhangCutType;
  readonly distance: number;
}

export const COMPONENT_SIDES = ['Left', 'Right'] as const;
export type ComponentSide = (typeof COMPONENT_SIDES)[number];

export interface JackSettings {
  readonly side: ComponentSide;
  readonly isOpen: boolean;
}

export interface PiggybackSettings {
  readonly purlinThickness: number;
  readonly runEndVerticalsThrough: boolean;
}

export interface AtticSettings {
  readonly middle: number;
  readonly width: number;
  readonly floorDepth: number;
  readonly useSingleFloorChord: boolean;
  readonly zoneSettings: ZoneSettings;
  readonly height: number;
}

export type CalculatedWebJointType = 'Chord' | 'Internal' | 'Heel' | 'KWebStepSide' | 'KWebNonStepSide';

export interface CalculatedWebJoint {
  readonly guid: string;
  readonly location: Point2D;
  readonly jointType: CalculatedWebJointType;
  readonly chordType: ChordType;
}

export type WebType = 'OrdinaryWeb' | 'HeelWeb' | 'KWeb' | 'KWebAndHeelWeb';

export interface WebSegment {
  readonly guid: string;
  readonly baseJointGuid: string;
  readonly endJointGuid: string;
  readonly webType: WebType;
  readonly cutByStepKWebGuid: string | null;
}

export interface WebbingRegion {
  readonly calculatedWebJoints: readonly CalculatedWebJoint[];
  readonly webSegments: readonly WebSegment[];
}

export interface WebbingInfo {
  readonly webbingRegions: readonly WebbingRegion[];
}

export interface MemberAnalysisInputs {
  readonly lumberList: LumberList;
  readonly lumberStockLengthPrices: readonly LumberStockLengthPrices[];
  readonly loadSettings: LoadSettings;
  readonly jackConnections: readonly JackGirderConnection[];
  readonly jackInformation: { readonly [componentDesignGuid: string]: JackInformation };
  readonly analysisSettings: AnalysisSettings;
  readonly additionalLoads: AdditionalLoads | null;
}

export interface MemberAnalysisInfo {
  readonly capacityRatio: number;
  readonly capacityRatiosAlongMembers: { readonly [memberGuid: string]: readonly number[] };
}

export interface StockLengthPrice {
  readonly stockLength: number;
  readonly price: number;
}

export interface LumberStockLengthPrices {
  readonly lumber: Lumber;
  readonly stockLengthPrices: readonly StockLengthPrice[];
}

interface JackReductionFactors {
  readonly topChord: number;
  readonly bottomChord: number;
}

interface JackBearing {
  readonly bearingGuid: string;
  readonly midpoint: Point2D;
  readonly type: BearingType;
}

interface JackBoundaryJoint {
  readonly analogJointGuid: string;
  readonly location: Point2D;
}

interface VerticalBearingReaction {
  readonly analogJointGuid: string;
  readonly loadType: LoadType;
  readonly yForce: number;
}

export interface JackInformation {
  readonly spacing: number;
  readonly thickness: number;
  readonly deadLoadReductionFactorsForUplift: JackReductionFactors | null;
  readonly bearings: readonly JackBearing[];
  readonly boundaryJoints: readonly JackBoundaryJoint[];
  readonly verticalBearingReactionsByLoadCase: readonly (readonly VerticalBearingReaction[])[];
  readonly loadTypes: readonly LoadType[];
}

export interface AnalysisHistoryRecord {
  readonly analysisSetGuid: string;
  readonly componentDesignGuid: string;
  readonly timestamp: string;
  readonly name: string;
}

export interface ComponentDesignWithInformation {
  readonly componentDesign: ComponentDesign;
  readonly history: readonly AnalysisHistoryRecord[];
  readonly timestamps: ComponentDesignTimestamps;
}

export interface ComponentDesignWithTimestamps {
  readonly componentDesign: ComponentDesign;
  readonly timestamps: ComponentDesignTimestamps;
}

export interface ComponentDesignTimestamps {
  readonly componentDesignGuid: string;
  readonly lastUpdated: string;
  readonly lastAnalyzed: string | null;
  readonly lastAnalyzedCapacity: number | null;
  readonly lastAnalysisHadErrors: boolean;
}

export interface ComponentDesignSealTimestamps {
  readonly componentDesignGuid: string;
  readonly analysisSetGuid: string;
  readonly jobId: number;
  readonly createdTimestamp: string;
  readonly returnedTimestamp: string | null;
}

export type SealStatus = 'none' | 'requested' | 'sealed';

export interface AnalogModel {
  readonly guid: string;
  readonly analogJoints: readonly AnalogJoint[];
  readonly analogLinks: readonly AnalogLink[];
  readonly panels: readonly AnalogPanel[];
}

export interface AnalogPanel {
  readonly name: string;
  readonly guid: string;
  readonly analogModelGuid: string;
  readonly linkGuids: readonly string[];
}

export interface AnalogLink {
  readonly guid: string;
  readonly analogModelGuid: string;
  readonly iJoint: AnalogJoint;
  readonly jJoint: AnalogJoint;
  readonly iFixity: Fixity;
  readonly jFixity: Fixity;
  readonly length: number;
  readonly angle: number;
  readonly isFictive: boolean;
  readonly memberStack: MemberStack;
  readonly name: string;
}

export interface Fixity {
  readonly momentRatio: number;
  readonly stiffnessLimitFactor: number | null;
}

export interface MemberStack {
  readonly guid: Guid;
  readonly midline: Segment2D | null;
  readonly memberType: MemberType;
  readonly memberGuids: readonly Guid[];
}

export const BOUNDARY_TYPES = [
  'Unrestrained',
  'HorizontalRollerWithUpwardRelease',
  'HorizontalRoller',
  'VerticalRoller',
  'Pinned',
  'PinnedWithUpwardRelease',
  'Fixed',
  'RotationFixed',
  'XandRFixed',
  'YandRFixed',
] as const;
export type BoundaryType = (typeof BOUNDARY_TYPES)[number];

export interface AnalogJoint {
  readonly guid: string;
  readonly analogModelGuid: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly boundaryType: BoundaryType;
  readonly isStructural: boolean;
}

export type WindDesignScope = 'NoWindLoads' | 'MWFRS' | 'ComponentsAndCladding';
type WindType = 'Wind' | 'Tornado';

export interface LoadCase {
  readonly guid: string;
  readonly name: string;
  readonly lumberDryDOL: number;
  readonly lumberWetDOL: number;
  readonly plateDOL: number;
  readonly index: number;
  readonly loads: readonly Load[];
  readonly windDesignScope: WindDesignScope;
  readonly windType: WindType;
  readonly isForUplift: boolean;
}
export enum AddLoadTo {
  LoadCasesWithSpecifiedLoadType = 'LoadCasesWithSpecifiedLoadType',
  StationaryLoadCasesWithSpecifiedLoadType = 'StationaryLoadCasesWithSpecifiedLoadType',
  SpecificLoadCase = 'SpecificLoadCase',
}

export interface ManualLoadInsertionBase {
  readonly loadInsertionMethod: LoadInsertionMethod;
}
export interface LoadDefinitionWrapper extends ManualLoadInsertionBase {
  readonly loadDefinition: ManualLoadDefinition;
  readonly loadInsertionMethod: 'ProjectedCoordinates';
}

export interface LoadWrapper extends ManualLoadInsertionBase {
  readonly load: ManualLoad;
  readonly loadInsertionMethod: 'Points';
}

export interface ManualLoadSettings {
  readonly guid: string;
  readonly loadCaseIdentification: AddLoadTo;
  readonly specifiedLoadCaseName: string | null;
  readonly load: LoadDefinitionWrapper | LoadWrapper;
}

export interface BCRestraintSegments {
  readonly fromLeft: boolean;
  readonly startPoint: number;
  readonly endPoint: number;
}

type Guid = string;
export type Distribution = 'Concentrated' | 'Distributed';

export type LoadType =
  'Unknown' | 'Dead' | 'LiveFloor' | 'Wind' | 'Tornado' | 'Rain' | 'Snow' | 'Seismic' | 'LiveRoof' | 'Drag' | 'Person';

export type MagnitudeUnit = 'Pounds' | 'PoundsPerSquareFoot' | 'PoundsPerLinearFoot';

interface LoadBase {
  readonly loadCaseGuid: Guid;
  readonly guid: Guid;
  readonly start: Point2D;
  readonly end: Point2D | null;
  readonly startMagnitude: number;
  readonly endMagnitude: number;
  readonly direction: Direction2D;
  readonly loadType: LoadType;
  readonly memberType: MemberType;
  readonly distribution: Distribution;
  readonly isGirderLoad: boolean;
  readonly isManualLoad: boolean;
  readonly isMovingLoad: boolean;
}
export interface ConcentratedLoad extends LoadBase {
  readonly distribution: 'Concentrated';
}

export interface DistributedLoad extends LoadBase {
  readonly distribution: 'Distributed';
  readonly end: Point2D;
}

export type Load = ConcentratedLoad | DistributedLoad;

interface ManualLoadBase {
  readonly guid: string;
  readonly start: Point2D;
  readonly startMagnitude: number;
  readonly endMagnitude: number;
  readonly magnitudeUnit: MagnitudeUnit;
  readonly direction: Direction2D;
  readonly loadType: LoadType;
  readonly memberType: MemberType;
  readonly distribution: Distribution;
  readonly name: string | null;
}

export interface ManualConcentratedLoad extends ManualLoadBase {
  readonly magnitudeUnit: 'Pounds';
  readonly distribution: 'Concentrated';
}

export interface ManualDistributedLoad extends ManualLoadBase {
  readonly end: Point2D;
  readonly magnitudeUnit: 'PoundsPerSquareFoot' | 'PoundsPerLinearFoot';
  readonly distribution: 'Distributed';
}

export type ManualLoad = ManualConcentratedLoad | ManualDistributedLoad;

export type CoordinateAxis = 'X' | 'Y';
export type LoadInsertionMethod = 'ProjectedCoordinates' | 'Points';

export type LoadDirection =
  | 'GlobalDown'
  | 'GlobalUp'
  | 'GlobalRight'
  | 'GlobalLeft'
  | 'NormalDown'
  | 'NormalUp'
  | 'ParallelRight'
  | 'ParallelLeft';

export type LoadMemberType = 'TopChord' | 'BottomChord' | 'Web' | 'LeftEndVertical' | 'RightEndVertical';

export const loadMemberTypes: LoadMemberType[] = [
  'TopChord',
  'BottomChord',
  'Web',
  'LeftEndVertical',
  'RightEndVertical',
];

interface ManualLoadDefinitionBase {
  readonly guid: string;
  readonly definitionAxis: CoordinateAxis;
  readonly loadDirection: LoadDirection;
  readonly loadType: LoadType;
  readonly memberType: LoadMemberType;
  readonly distribution: Distribution;
  readonly name: string | null;
}

export interface ManualConcentratedLoadDefinition extends ManualLoadDefinitionBase {
  readonly coordinate: number;
  readonly magnitude: number;
  readonly distribution: 'Concentrated';
}

export interface ManualDistributedLoadDefinition extends ManualLoadDefinitionBase {
  readonly startCoordinate: number;
  readonly endCoordinate: number;
  readonly startMagnitude: number;
  readonly endMagnitude: number;
  readonly magnitudeUnit: 'PoundsPerSquareFoot' | 'PoundsPerLinearFoot';
  readonly isProjected: boolean;
  readonly distribution: 'Distributed';
}

export type ManualLoadDefinition = ManualConcentratedLoadDefinition | ManualDistributedLoadDefinition;

export interface Direction {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const connectorTypes = ['Special', 'Hanger'] as const;
export type ConnectorType = (typeof connectorTypes)[number];

interface ConnectorBase {
  readonly guid: string;
  readonly connectorType: ConnectorType;
}

interface SpecialConnector extends ConnectorBase {
  readonly connectorType: 'Special';
}

interface HangerConnector extends ConnectorBase {
  readonly connectorType: 'Hanger';
  readonly configurationGuid: string;
}

export type Connector = SpecialConnector | HangerConnector;

export const bearingAnchors = ['Center', 'Left', 'Right'] as const;
export type BearingAnchor = (typeof bearingAnchors)[number];

interface BearingBase {
  readonly name: string;
  readonly guid: string;
  readonly depth: number;
  readonly bearingType: BearingType;
  readonly bearingCondition: BearingCondition;
  readonly releaseForUplift: boolean;
  readonly connectors: readonly Connector[];
}

export interface AbsoluteBearing extends BearingBase {
  readonly relativeGeometry: null;
  readonly width: number;
  readonly centerPointX: number;
  readonly centerPointY: number;
  readonly normal: Direction2D;
  readonly anchor: BearingAnchor;
}

export interface RelativeBearing extends BearingBase {
  readonly relativeGeometry: RelativeBearingsGeometry;
}

// FIXME It would be cleaner to rename `relativeGeometry` to `geometry` and have all of the absolute geometry properties
//   be included there so that the server could differentiate between an absolute and relative bearing as a
//   discriminated union on the `geometry` property.
export type Bearing = AbsoluteBearing | RelativeBearing;

export function isRelativeBearing(bearing: Bearing): bearing is RelativeBearing {
  return !!bearing.relativeGeometry;
}

export type GeneratableBearingAnchor = 'Left' | 'Center' | 'Right';

export type GeneratableBearingType = 'HorizontalGeneratableBearing' | 'VerticalGeneratableBearing';

export interface GeneratableBearingBase {
  readonly generatableBearingType: GeneratableBearingType;
  readonly guid: string;
  readonly offset: number;
  readonly width: number;
  readonly chordLocation: ChordType;
  readonly bearingType: BearingType;
  readonly offsetSide: ComponentSide;
  readonly connectors: readonly Connector[];
}

export interface HorizontalGeneratableBearing extends GeneratableBearingBase {
  readonly generatableBearingType: 'HorizontalGeneratableBearing';
  readonly anchor: GeneratableBearingAnchor;
  readonly isContinuous: boolean;
}

export interface VerticalGeneratableBearing extends GeneratableBearingBase {
  readonly generatableBearingType: 'VerticalGeneratableBearing';
}

export type GeneratableBearing = HorizontalGeneratableBearing | VerticalGeneratableBearing;

export const allHangerHardware = [
  'HUS26',
  'HUS28',
  'HHUS26_2',
  'HHUS28_2',
  'LTHJA26',
  'LUS24',
  'LUS24_2',
  'LUS26',
  'LUS28',
  'MUS26',
  'MUS28',
  'THJA26',
  'TJC37',
] as const;
export type StandardConnectingHardwareModel = (typeof allHangerHardware)[number];

interface ConnectingHardwareBase {
  readonly standardOrCustom: 'Standard' | 'Custom';
}

export interface StandardConnectingHardware extends ConnectingHardwareBase {
  readonly standardOrCustom: 'Standard';
  readonly model: StandardConnectingHardwareModel;
}

export interface CustomConnectingHardware extends ConnectingHardwareBase {
  readonly standardOrCustom: 'Custom';
  readonly name: string;
}

export type ConnectingHardware = StandardConnectingHardware | CustomConnectingHardware;

export type HangerLocationFace = 'FrontFace' | 'BackFace';

export interface Hanger {
  readonly guid: string;
  readonly connector: ConnectingHardware;
  readonly locationOnGirder: Point2D;
  readonly locationType: HangerLocationFace;
}

export type PlatePlacement = 'FrontBack' | 'TopBottom';

export type ComboPlateType = (typeof COMBO_PLATE_TYPES)[number];
export const COMBO_PLATE_TYPES = ['Combo16', 'Combo18', 'Combo18HS', 'Combo20', 'Combo20HS'] as const;
export type PlateType = (typeof PLATE_TYPES)[number];
export const PLATE_TYPES = [
  'M20',
  'M16',
  'M20HS',
  'M18SHS',
  'MII16',
  'M18AHS',
  'MII20',
  'MII20HS',
  'MT18HS',
  'MT20HS',
  'MTH18',
  'SMH18',
  'Eagle16',
  'Eagle18',
  'Eagle20',
  'Eagle18HS',
  'Eagle20HS',
  'TL16',
  'TL18',
  'TL20',
  'MT16',
  'MT18',
  'MT20',
  'AS18HS',
  'AS18S6',
  'AS18S5',
  'AS20',
  'AS20HS',
  'AlpineW',
  'Alpine20H',
  'AlpineT',
  'Alpine18SS',
  'CH18',
  'CH18HS',
  'CH20',
  'CH20S',
  'CherokeeB',
  'Any18HS',
  'Any18',
  'Any20',
  'Any20HS',
  ...COMBO_PLATE_TYPES,
] as const;

export interface PlateSize {
  readonly width: number;
  readonly length: number;
}

export interface PlatePair {
  readonly unmergedShearLines: readonly ShearLine[];
  readonly mergedShearLines: readonly ShearLine[];
  readonly guid: string;
  readonly angleX: number;
  readonly angleY: number;
  readonly angleZ: number;
  readonly name: string;
  readonly centerX: number;
  readonly centerY: number;
  readonly centerZ: number;
  readonly distanceBetweenPlates: number;
  readonly fabricationTolerancePercentage: number | null;
  readonly platePlacement: PlatePlacement;
  readonly plateType: PlateType;
  readonly tangiblePlateTypesForComboPlateType: readonly PlateType[];
  readonly width: number;
  readonly length: number;
  readonly geometryType: PlateGeometryType;
  readonly hingeAngle: number | null;
}

export type PlateGeometryType = 'Rectangular' | 'TPlate' | 'BevelPlateLeftHanded' | 'BevelPlateRightHanded' | 'Hinged';

export interface PlateTypeProperties {
  readonly baseThickness: number;
  readonly thickness: number;
  readonly plateManufacturer: PlateManufacturer;
  readonly plateType: PlateType;
}

export interface ShearLine {
  readonly guid: string;
  readonly type: string;
  readonly start: Point2D;
  readonly end: Point2D;
  readonly openAreas: readonly Point2D[][];
}

export interface Point2D {
  readonly x: number;
  readonly y: number;
}

export interface DxfLayout {
  readonly name: string;
  readonly paths: readonly (readonly Point2D[])[];
}

export type BraceInstallationLocation = 'Back' | 'Center' | 'Front';

export type BraceType = 'Scab' | 'ExtraplanarBrace';

export type MemberType =
  // Truss member types
  | 'Web'
  | 'EndVertical'
  | 'Slider'
  | 'Wedge'
  | 'SpliceBlock'
  | 'BearingBlock'
  | 'NonstructuralBlock'
  | 'GableStud'
  | 'ExtraplanarBrace'
  | 'Scab'
  | 'RibbonBlock'
  | 'TrimmableEnd'
  | 'Unknown'

  // Wall member types
  | 'Column'
  | 'CapChord'
  | 'Stud'
  | 'Cripple'
  | 'BeamStud'
  | 'Tee'
  | 'Corner'
  | 'LadderTee'
  | 'LetIn'
  | 'TrimStud'
  | 'Packer'
  | 'Header'
  | 'Sill'
  | 'Sheathing'

  // Shared
  | 'TopChord'
  | 'BottomChord';

export interface Member {
  readonly guid: string;
  readonly lumber: Lumber;
  /** This might not be current/valid if relativeGeometry is nonnull. */
  readonly geometry: readonly Point2D[];
  readonly relativeGeometry: RelativeMemberGeometry | null;
  readonly name: string;
  readonly assemblyName: string | null;
  readonly memberType: MemberType;
  readonly angle: number;
  readonly pitch: string;
  readonly crossSectionalArea: string;
  /** @deprecated This might not be current/correct if relativeGeometry is nonnull. */
  readonly midlineStartX: number;
  /** @deprecated This might not be current/correct if relativeGeometry is nonnull. */
  readonly midlineStartY: number;
  /** @deprecated This might not be current/correct if relativeGeometry is nonnull. */
  readonly midlineStartZ: number;
  /** @deprecated This might not be current/correct if relativeGeometry is nonnull. */
  readonly midlineEndX: number;
  /** @deprecated This might not be current/correct if relativeGeometry is nonnull. */
  readonly midlineEndY: number;
  /** @deprecated This might not be current/correct if relativeGeometry is nonnull. */
  readonly midlineEndZ: number;
  readonly thickness: number;
  readonly leftBevelCut: BevelCut | null;
  readonly rightBevelCut: BevelCut | null;
  readonly bottomBevelCut: BottomBevelCut | null;
  readonly isFieldInstalled: boolean;
}

export interface Lumber {
  readonly nominalThickness: string;
  readonly nominalWidth: string;
  readonly actualThickness: number;
  readonly actualWidth: number;
  readonly grade: LumberGrade;
  readonly tangibleGrades: readonly LumberGrade[];
  readonly species: LumberSpecies;
  readonly tangibleSpecies: readonly LumberSpecies[];
  readonly treatmentType: LumberTreatmentType;
  readonly gradingMethod: string;
  readonly structure: 'Sawn' | 'StructuralComposite';
}

export interface Gusset {
  readonly guid: string;
  readonly material: string;
  readonly geometry: readonly Point2D[];
}

export interface DimensionSet extends BlobStorageStorable {
  readonly guid: string;
  readonly distanceDimensionLines: readonly DistanceDimensionLine[];
}

export interface DistanceDimensionLine {
  readonly guid: string;
  readonly points: readonly DimensionPoint[];
  readonly isContextDependent: boolean;
}

export interface DimensionPoint extends Point2D {
  readonly categories: readonly DimensionPointCategory[];
}

export interface UserDistanceDimension {
  readonly start: Point2D;
  readonly end: Point2D;
}

export interface LayoutUserDistanceDimension extends UserDistanceDimension {
  readonly levelGuid: string | null;
}

export interface LumberPropertySet {
  readonly specificGravity: number;
  readonly bending: number;
  readonly tensionParallelToGrain: number;
  readonly shearParallelToGrain: number;
  readonly compressionPerpendicularToGrain: number;
  readonly compressionParallelToGrain: number;
  readonly modulusOfElasticity: number;
  readonly modulusOfElasticityMin: number;
}

export interface PlateMaterialProperties {
  readonly teethPerSquareInch: number;
  readonly tensileEfficiencyAt0_MaximumNetSection: number;
  readonly tensileEfficiencyAt90_MaximumNetSection: number;
  readonly tensileEfficiencyAt0_MinimumNetSection: number;
  readonly tensileEfficiencyAt90_MinimumNetSection: number;
  readonly tensionAt0_MinimumNetSection: number;
  readonly tensionAt0_MaximumNetSection: number;
  readonly tensionAt90_MinimumNetSection: number;
  readonly tensionAt90_MaximumNetSection: number;
  readonly shearEfficiencyAt0: number;
  readonly shearEfficiencyAt30: number;
  readonly shearEfficiencyAt60: number;
  readonly shearEfficiencyAt90: number;
  readonly shearEfficiencyAt120: number;
  readonly shearEfficiencyAt150: number;
  readonly allowableShearAt0: number;
  readonly allowableShearAt30: number;
  readonly allowableShearAt60: number;
  readonly allowableShearAt90: number;
  readonly allowableShearAt120: number;
  readonly allowableShearAt150: number;
  readonly tensileYieldStrength: number;
  readonly tensileUltimateStrength: number;
}

export interface GripDesignSet {
  readonly source: string;
  readonly plate: PlateType;
  readonly lumber: LumberSpecies;
  readonly aa: number;
  readonly ea: number;
  readonly ae: number;
  readonly ee: number;
  readonly netAreaEndOffset?: number;
  readonly netAreaEdgeOffset?: number;
}

export interface PlateGripSummary {
  readonly guid: string;
  readonly aa: string;
  readonly ea: string;
  readonly ae: string;
  readonly ee: string;
  readonly plateType: PlateType;
  readonly species: LumberSpecies;
  readonly tangibleSpecies: readonly LumberSpecies[];
  readonly lumberTreatmentType: LumberTreatmentType;
}

export interface PostDistanceDimension {
  readonly dimensionSetGuid: string;
  readonly name: string;
  readonly startPointX: number;
  readonly startPointY: number;
  readonly startPointZ: number;
  readonly endPointX: number;
  readonly endPointY: number;
  readonly endPointZ: number;
}

export interface Point3D {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface Point4D {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly w: number;
}

export interface ProjectSummary {
  readonly guid: string;
  readonly name: string;
  readonly createdAt: string;
}

// in user-visible order
export const PROJECT_STATUSES = [
  'New',
  'Estimating',
  'Designing',
  'Sealing',
  'Production',
  'Shipping',
  'Delivered',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export interface ProjectModelProperties {
  readonly guid: string;
  readonly archived: boolean;
  readonly organization: string;
  readonly name: string;
  readonly materialSelection: MaterialSelection;
  readonly createdAt: string;
  readonly region: string | null;
  readonly designProgram: string | null;
  readonly contactName: string;
  readonly contactPhone: string;
  readonly contactEmail: string;
  readonly customerName: string | null;
  readonly lotNumber: string | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly subdivision: string | null;
  readonly model: string | null;
  readonly buildingDesignerName: string | null;
  readonly buildingDesignerAddress: string | null;
  readonly buildingDesignerCity: string | null;
  readonly buildingDesignerState: string | null;
  readonly buildingDesignerLicenseNumber: string | null;
  readonly projectSalesman: string | null;
  readonly assigneeEmail: string | null;
  readonly dueDate: string | null; // YYYY-MM-DD
  readonly status: ProjectStatus;
  readonly endConditionPresetsLeft: readonly EndConditionSettings[];
  readonly endConditionPresetsRight: readonly EndConditionSettings[];
}

export interface Project extends ProjectModelProperties {
  readonly layoutModels: { [guid: string]: LayoutModel };
  readonly insertions: readonly Insertion[];
  readonly trussGroups: { [guid: string]: TrussGroup };
}

export interface NewTrussGroup {
  readonly parentGuid: string | null;
  readonly name: string;
  readonly trussEnvelopeGuids: readonly string[];
}

export interface TrussGroup extends NewTrussGroup {
  readonly guid: string;
}

export interface ComponentDesignSummary {
  readonly guid: string;
  readonly component: ComponentSummary;
  readonly materialSelection: MaterialSelection;
  readonly componentDesignTimestamps: ComponentDesignTimestamps;
  readonly analogLocked: boolean;
  readonly hangersLocked: boolean;
  readonly loadingLocked: boolean;
  readonly membersLocked: boolean;
  readonly platingLocked: boolean;
}

export interface ComponentSummary {
  readonly name: string;
  readonly componentType: TrussType;
}

export interface ComponentDesignSummariesAndTimestamps {
  readonly componentDesignSummaries: readonly ComponentDesignSummary[];
  readonly timestamps: readonly ComponentDesignTimestamps[];
}

export interface WallTemplate {
  readonly guid: string;
  readonly name: string;
  readonly wallOption: string;
  readonly wallThickness: number;
  readonly maxLength: number;
  readonly partyWall: boolean;
  readonly splicePlate: boolean;
  readonly splicePlateLength: number;
  readonly cornerStyle: CornerStyle;
  readonly tStyle: TStyle;
  readonly kingStudCount: number;
  readonly sillCount: number;
  readonly minHeaderSize: number;
  readonly studDropDistance: number;
  readonly endAngle: number;
  readonly sheathing: boolean;
  readonly sheathingTypes: readonly SheathingType[];
  readonly sheathingOverlap: number;
  readonly height: number;
  readonly cornerBrace: boolean;
  readonly letIn: boolean;
  readonly letInHeight: number;
  readonly letInType: string;
  readonly bottomPlate: Lumber;
  readonly topPlate: Lumber;
  readonly packerLumber: Lumber;
  readonly tLumber: Lumber;
  readonly cornerLumber: Lumber;
  readonly capPlateLumber: Lumber;
  readonly studLumber: Lumber;
  readonly capPlate: boolean;
  readonly studSpacing: number;
  readonly studPattern: string;
  readonly vaporBarrier: boolean;
  readonly vaporBarrierSize: number;
}

export interface CornerStyle {
  readonly guid: string;
  readonly name: string;
  readonly blockThickness: number;
  readonly blockDouble: boolean;
  readonly blockSpacing: number;
  readonly studNumber: number;
  readonly studSize: number;
}

export interface TStyle {
  readonly guid: string;
  readonly name: string;
  readonly wallThickness: number;
  readonly blockSpacing: number;
  readonly orientation: number;
}

export interface WindowTemplate {
  readonly guid: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly floorNumber: number;
  readonly packerCount: number;
  readonly trimStudCount: number;
  readonly headerHeight: number;
  readonly headerLumber: Lumber;
  readonly headerMaterial: string;
}

export interface DoorTemplate {
  readonly guid: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly headerLumber: Lumber;
  readonly headerFiller: string;
  readonly floorNumber: number;
  readonly packerCount: number;
  readonly trimStudCount: number;
}

export interface Building {
  readonly modelType: 'Building';
  readonly guid: string;
  readonly name: string;
  readonly materialSelection: MaterialSelection;
}

export interface Insertion {
  readonly type: 'HierarchyModel' | 'NormalComponent' | 'LayoutComponent';
  readonly parentGuid: string;
  readonly objectGuid: string;
}

export function insertionsEqual(a: Insertion, b: Insertion): boolean {
  return a.type === b.type && a.parentGuid === b.parentGuid && a.objectGuid === b.objectGuid;
}

export interface InsertionPosition {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotation: number;
}

export interface Positioned<T> {
  readonly item: T;
  readonly positions: readonly InsertionPosition[];
}

export interface Level {
  readonly modelType: 'Level';
  readonly guid: string;
  readonly index: number;
  readonly name: string;
  readonly materialSelection: MaterialSelection;
  readonly plateElevation: number;
  readonly wallHeight: number;
  readonly floorHeight: number;
}

export interface RoofPlaneDefinition {
  readonly guid: string;
  readonly name: string;
  readonly pitch: number;
  readonly heelHeight: number;
  readonly buttCut: number;
  readonly overhang: number;
  readonly fasciaWidth: number;
  readonly fasciaThickness: number;
  readonly subfasciaWidth: number;
  readonly subfasciaThickness: number;
  readonly deckingThickness: number;
  readonly topChordWidth: number;
  readonly elevation: number;
  readonly cantilever: number;
  readonly bearingWidth: number;
}

export type LineLocation = 'Center' | 'Port' | 'Starboard';

interface CalculatedLineBase {
  readonly guid: string;
  readonly lineType: 'Absolute' | 'Parallel' | 'PointSlope' | 'PointToPoint' | 'Perpendicular' | 'Division' | 'Member';
}
export type CalculatedLine =
  ParallelLine | PerpendicularLine | PointSlopeLine | PointToPointLine | DivisionLine | MemberLine | AbsoluteLine;

export interface AbsoluteLine extends CalculatedLineBase {
  readonly lineType: 'Absolute';
  readonly line: Line2D;
  readonly name: string;
}

export interface ParallelLine extends CalculatedLineBase {
  readonly lineType: 'Parallel';
  readonly lineGuid: string;
  readonly offset: number;
  readonly name: string;
}

export interface PointSlopeLine extends CalculatedLineBase {
  readonly lineType: 'PointSlope';
  readonly line1Guid: string;
  readonly line2Guid: string;
  readonly direction: Direction2D;
  readonly offset: number;
  readonly name: string;
}

export interface PointToPointLine extends CalculatedLineBase {
  readonly lineType: 'PointToPoint';
  readonly basePointLine1Guid: string;
  readonly basePointLine2Guid: string;
  readonly endPointLine1Guid: string;
  readonly endPointLine2Guid: string;
  readonly offset: number;
  readonly name: string;
}

export interface PerpendicularLine extends CalculatedLineBase {
  readonly lineType: 'Perpendicular';
  readonly basePointLine1Guid: string;
  readonly basePointLine2Guid: string;
  readonly lineGuid: string;
  readonly offset: number;
  readonly name: string;
}

export interface DivisionLine extends CalculatedLineBase {
  readonly lineType: 'Division';
  readonly line1Guid: string;
  readonly line2Guid: string;
  readonly divisionFactor: number;
  readonly name: string;
}

export interface MemberLine extends CalculatedLineBase {
  readonly lineType: 'Member';
  readonly memberGeometryGuid: string;
  readonly lineLocation: LineLocation;
}

interface BevelCutBase {
  readonly cutType: 'Single' | 'Double';
}

export interface SingleBevelCut extends BevelCutBase {
  readonly cutType: 'Single';
  readonly cutSide: 'Front' | 'Back';
  readonly angle: number;
}

export interface DoubleBevelCut extends BevelCutBase {
  readonly cutType: 'Double';
  readonly frontAngle: number;
  readonly backAngle: number;
}

export type BevelCut = SingleBevelCut | DoubleBevelCut;

export interface BottomBevelCut {
  readonly cutSide: 'Front' | 'Back';
  readonly angle: number;
}

interface RelativeGeometryBase {
  readonly guid: string;
  readonly geometryType: 'Member' | 'Bearing';
}
export type RelativeGeometry = RelativeMemberGeometry | RelativeBearingsGeometry;

export interface RelativeBearingsGeometryBase extends RelativeGeometryBase {
  readonly geometryType: 'Bearing';
  readonly bearingGeometryType: 'Bearing' | 'ContinuousBearing';
  readonly bearingLineGuid: string;
  readonly directionReversed: boolean;
  readonly bearingName: string;
}
export type RelativeBearingsGeometry = RelativeBearingGeometry | RelativeContinuousBearingGeometry;

export interface RelativeBearingGeometry extends RelativeBearingsGeometryBase {
  readonly bearingGeometryType: 'Bearing';
  readonly width: number;
  readonly lineGuid: string;
  readonly lineLocation: LineLocation;
}

export interface RelativeContinuousBearingGeometry extends RelativeBearingsGeometryBase {
  readonly bearingGeometryType: 'ContinuousBearing';
  readonly leftSideLineGuid: string;
  readonly rightSideLineGuid: string;
}

interface RelativeMemberGeometryBase extends RelativeGeometryBase {
  readonly geometryType: 'Member';
  readonly isBrace: boolean;
  readonly width: number;
  readonly lineGuid: string;
  readonly lineLocation: LineLocation;
  readonly baseCutGuids: readonly string[];
  readonly endCutGuids: readonly string[];
  readonly memberName: string;
}

export interface RelativeNonBraceMemberGeometry extends RelativeMemberGeometryBase {
  readonly isBrace: false;
}

export interface RelativeBraceGeometry extends RelativeMemberGeometryBase {
  readonly isBrace: true;
  readonly braceType: BraceType;
  readonly braceInstallationLocation: BraceInstallationLocation;
  readonly side: LineLocation;
  readonly bracedMemberGuid: string;
}

export type RelativeMemberGeometry = RelativeNonBraceMemberGeometry | RelativeBraceGeometry;

export const bearingTypes = ['DoublePlate', 'ConcreteWall', 'Hanger', 'Ledger', 'Header', 'ToeNail'] as const;
export type BearingType = (typeof bearingTypes)[number];

export const bearingConditions = ['Automatic', 'Pinned', 'HorizontalRoller', 'VerticalRoller'] as const;
export type BearingCondition = (typeof bearingConditions)[number];

export interface EndConditionSettings {
  readonly guid: string;
  readonly endVerticalCount: number;
  readonly topChordSlider: boolean;
  readonly topChordSliderLength: number;
  readonly fireCutDistance: number;
  readonly topRibbonWidth: number;
  readonly topRibbonHeight: number;
  readonly bottomRibbonWidth: number;
  readonly bottomRibbonHeight: number;
  readonly bearingBlockWidth: number;
  readonly bearingBlockElevation: number;
  readonly measureBearingBlockFromTop: boolean;
  readonly bottomChordCutBackDistance: number;
  readonly topChordCutBackDistance: number;
  readonly noBearing: boolean;
  readonly isTopChordBearing: boolean;
  readonly bearingGap: number;
  readonly bearingType: BearingType;
  readonly bearingWidth: number;
  readonly parapetHeight: number;
}

export type ChordType = 'Top' | 'Bottom';

/**
 * The desired method for calculating the heel height. The heel height method is just the total height of the heel. The
 * butt cut method defines the height of the vertical cut for the bottom chord at the heel. The total heel height could
 * then be calculated based on the slope and vertical width of the top chord at the heel.
 */
export const HEEL_METHODS = ['HeelHeight', 'ButtCut'] as const;
export type HeelMethod = (typeof HEEL_METHODS)[number];

export interface HeelSettingsBase {
  readonly heelMethod: HeelMethod;
  /**
   * Specifies the desired height of an extra vertical cut applied to the bottom chord. Only applies if the top chord is
   * run through and the heel height is less than or equal to the vertical width of the top chord.
   */
  readonly interiorButtCut: number;
  readonly chordTypeRunThrough: ChordType;
}

export interface HeelHeightMethodHeelSettings extends HeelSettingsBase {
  readonly heelMethod: 'HeelHeight';
  readonly heelHeight: number;
}

export interface ButtCutMethodHeelSettings extends HeelSettingsBase {
  readonly heelMethod: 'ButtCut';
  readonly buttCut: number;
}

export type HeelSettings = HeelHeightMethodHeelSettings | ButtCutMethodHeelSettings;

export type HeelReinforcementType = 'None' | 'Wedge' | 'TopChordSlider' | 'BottomChordSlider';

export interface BeamPocketSettings {
  readonly type: 'beamPocket';
  readonly guid: string;

  readonly distanceFromLeft: number;
  readonly bearingWidth: number;

  readonly pocketWidth: number;
  readonly pocketHeight: number;

  readonly bottomChordCutOut: boolean;
  readonly leftVerticalsCount: number;
  readonly rightVerticalsCount: number;
}

export interface VerticalStackSettings {
  readonly type: 'verticalStack';
  readonly guid: string;

  readonly distanceFromLeft: number;
  readonly bearingWidth: number;

  readonly hasBearing: boolean;
  readonly verticalsCount: number;
}

export type StepDirection = 'Up' | 'Down';

export interface StepSettings {
  readonly type: 'step';
  readonly guid: string;

  readonly distanceFromLeft: number;
  readonly stepDistance: number;

  readonly stepDirection: StepDirection;
}

export interface TraySettings {
  readonly type: 'tray';
  readonly guid: string;

  readonly distanceFromLeft: number;
  readonly stepUp: number;
  readonly trayWidth: number;
  readonly stepDown: number;
}

export type InteriorConditionSettings = BeamPocketSettings | VerticalStackSettings | StepSettings | TraySettings;

export const CHASE_TYPES = ['VariableWidth', 'FixedWidth', 'None'] as const;
export type ChaseType = (typeof CHASE_TYPES)[number];

export const CHASE_LOCATION_TYPES = ['Floating', 'Centered', 'Fixed'] as const;
export type ChaseLocationType = (typeof CHASE_LOCATION_TYPES)[number];

export interface ChaseSettings {
  readonly guid: string;
  readonly noChase: boolean;
  readonly chaseType: ChaseType;
  readonly chaseMaxWidth: number;
  readonly chaseMinWidth: number;
  readonly chaseLocationType: ChaseLocationType;
  readonly chaseBaseSide: ComponentSide;
  readonly chaseLocation: number;
}

export const WEBBING_START_VALUES = ['Bearing', 'Top', 'Bottom'] as const;
export type WebbingStart = (typeof WEBBING_START_VALUES)[number];

export const WEBBING_TYPES = ['Warren', 'K'] as const;
export type WebbingType = (typeof WEBBING_TYPES)[number];

export interface ZoneSettings {
  readonly guid: string;
  readonly horizontalWebLength: number;
  readonly maxWebRun: number;
  readonly webbingType: WebbingType;
  readonly leftWebbingStart: WebbingStart;
  readonly rightWebbingStart: WebbingStart;
  readonly chaseSettings: ChaseSettings;
}

export const GABLE_STUDDING_DIRECTIONS = [
  'LeftToRight',
  'RightToLeft',
  'LocationOutward',
  'EndsInward',
  'CenterOutward',
] as const;
export type GableStuddingDirection = (typeof GABLE_STUDDING_DIRECTIONS)[number];

export interface GableSettings {
  readonly isGable: boolean;
  readonly isStructuralGable: boolean;
  readonly studSpacing: number;
  readonly studdingDirection: GableStuddingDirection;
  readonly studStartLocation: number;
  readonly minimumStudLength: number;
}

interface TrussSettingsBase {
  readonly name: string;
  readonly componentType: ComponentType;
  readonly gableSettings: GableSettings;
  readonly definitionType: TrussDefinitionType;
}

export interface FeaturePatternTrussSettings extends TrussSettingsBase {
  readonly definitionType: 'FeaturePattern';
  readonly plies: number;
  readonly spacing: number;
  readonly width: number;
  readonly depth: number;
  readonly thickness: number;
  readonly leftEndSettings: EndConditionSettings;
  readonly rightEndSettings: EndConditionSettings;
  readonly interiorConditionSettings: readonly InteriorConditionSettings[];
  readonly zoneSettings: readonly ZoneSettings[];
  readonly topChordSlope: number;
  readonly bottomChordSlope: number;
  readonly continuouslyBearing: boolean;
}

export interface FeaturePatternTrussContainerSlopeSettings {
  readonly edgeToSlopeFrom: Segment2D;
  readonly topSlopeInDegrees: number;
  readonly bottomSlopeInDegrees: number;
}

export interface FeaturePatternTrussContainer {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly name: string;
  readonly shape: FloorPolyline3D;
  readonly slopeSettings: FeaturePatternTrussContainerSlopeSettings | null;
  readonly featureAreas: readonly LayoutFeatureArea[];
  readonly exclusions: readonly ExclusionLayoutFeature[];
}

export interface FeaturePatternTrussEnvelopeSettings {
  readonly kneeWall: boolean;
}

export type AbsoluteFloorPolylineSegmentGeometry = {
  readonly type: 'AbsoluteVertical';
  readonly segment: Segment2D;
  readonly isInfinite: boolean;
};

export type AgainstBearingFloorPolylineSegmentGeometry = {
  readonly type: 'AgainstBearing';
  readonly bearingEnvelopeGuid: string;
  readonly flipped: boolean;
};

export type FloorPolylineSegmentGeometry =
  AbsoluteFloorPolylineSegmentGeometry | AgainstBearingFloorPolylineSegmentGeometry;

export interface FloorPolyline3D {
  readonly geometry: readonly FloorPolylineSegmentGeometry[];
  readonly bottomElevation: number;
  readonly topElevation: number;
}

export interface Rectangle2D {
  readonly type: 'Rectangle2D';
  readonly position: Point2D;
  readonly verticalAnchor: 'Upper' | 'Lower' | 'Center';
  readonly horizontalAnchor: 'Left' | 'Right' | 'Center';
  readonly width: number;
  readonly length: number;
  readonly rotationAngle: number;
}

export interface Box3D {
  readonly type: 'Box3D';
  readonly position: Point2D;
  readonly verticalAnchor: 'Upper' | 'Lower' | 'Center';
  readonly horizontalAnchor: 'Left' | 'Right' | 'Center';
  readonly width: number;
  readonly length: number;
  readonly rotationAngle: number;
  readonly bottomElevation: number;
  readonly topElevation: number;
}

export interface Circle2D {
  readonly type: 'Circle2D';
  readonly center: Point2D;
  readonly radius: number;
}

export type Cylinder3D = Omit<Circle2D, 'type'> & {
  readonly type: 'Cylinder3D';
  readonly bottomElevation: number;
  readonly topElevation: number;
};

export type LayoutFeatureShape = Rectangle2D | Box3D | Circle2D | Cylinder3D;

export interface LayoutFeatureAreaBase {
  // named `featureType` to avoid conflicting with the `type` property -- which is 'featureArea' for all of these --
  //   used extensively for top level objects in the design client
  readonly featureType: 'Chase' | 'Step' | 'BeamPocket' | 'VerticalStack';

  readonly guid: string;
  readonly area: Rectangle2D;
}

export interface ChaseLayoutFeatureArea extends LayoutFeatureAreaBase {
  readonly featureType: 'Chase';
  readonly maxWidth: number | null;
  readonly minWidth: number | null;
}

export interface StepLayoutFeatureArea extends LayoutFeatureAreaBase {
  readonly featureType: 'Step';
  readonly height: number;
}

export interface BeamPocketLayoutFeatureArea extends LayoutFeatureAreaBase {
  readonly featureType: 'BeamPocket';
  readonly beamWidth: number | null;
  readonly pocketHeight: number;
  readonly bottomChordCutOut: boolean;
  readonly leftVerticalsCount: number;
  readonly rightVerticalsCount: number;
}

export interface VerticalStackLayoutFeatureArea extends LayoutFeatureAreaBase {
  readonly featureType: 'VerticalStack';
  readonly bearingWidth: number | null;
  readonly verticalsCount: number;
}

export interface ExclusionLayoutFeature {
  readonly guid: string;
  readonly name: string;
  readonly shape: LayoutFeatureShape;
}

export type LayoutFeatureArea =
  ChaseLayoutFeatureArea | StepLayoutFeatureArea | BeamPocketLayoutFeatureArea | VerticalStackLayoutFeatureArea;

export interface ChordBracing {
  readonly slopedTopChordBracingSettings: ChordBracingSettings;
  readonly bottomChordBracingSettings: ChordBracingSettings;
  readonly flatTopChordBracingSettings: ChordBracingSettings;
}

export interface ChordBracingSettings {
  readonly isSheathed: boolean;
  readonly useMaxPurlinSpacing: boolean;
  readonly purlinSpacing: number;
}

export interface Line2D {
  readonly basePoint: Point2D;
  readonly direction: Direction2D;
}

export interface Direction2D {
  readonly x: number;
  readonly y: number;
}

export interface Segment2D {
  readonly basePoint: Point2D;
  readonly endPoint: Point2D;
}

export interface Direction3D {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface Plane3D {
  readonly normalDirection: Direction3D;
  // A point on the plane.
  readonly basePoint: Point3D;
}

export interface LumberWithStockLengths {
  readonly lumber: Lumber;
  readonly stockLengths: readonly number[];
}

export interface LumberWithStockLength extends Lumber {
  stockLength: number;
}

export interface LumberListPrototype {
  readonly guid: string;
  readonly name: string;
  readonly isArchived: boolean;
  readonly lumberSets: LumberSet[];
}

export interface LumberList {
  readonly guid: string;
  readonly name: string;
  readonly isArchived: boolean;
  readonly stockLumbers: readonly LumberWithStockLengths[];
}

export interface PlateList {
  readonly guid: string;
  readonly name: string;
  readonly isArchived: boolean;
  readonly plates: readonly PlateOption[];
  readonly useComboPlates: boolean;
}

export interface HangerList {
  readonly guid: string;
  readonly name: string;
  readonly isArchived: boolean;
  readonly hangerHardware: readonly StandardConnectingHardwareModel[];
}

export interface PlateOption {
  readonly plateType: PlateType;
  readonly tangiblePlateTypesForComboPlateType: readonly PlateType[];
  readonly width: number;
  readonly length: number;
  readonly geometryType: PlateGeometryType;
}

export interface OrganizationMaterialSetPrototype extends BlobStorageStorable {
  readonly organization: string;

  readonly defaultLumberListGuid: string;
  readonly defaultPlateListGuid: string;

  readonly lumberLists: { readonly [guid: string]: LumberListPrototype };
  readonly plateLists: { readonly [guid: string]: PlateList };
  readonly hangerLists: { readonly [guid: string]: HangerList };
}

export interface OrganizationMaterialSet {
  readonly organization: string;

  readonly defaultMaterials: MaterialSelection;

  readonly lumberLists: { readonly [guid: string]: LumberList };
  readonly plateLists: { readonly [guid: string]: PlateList };
  readonly hangerLists: { readonly [guid: string]: HangerList };
}

// these fields exist (spread) on the `organization_presets` table -- update presets if you add any more list types here
export interface MaterialSelection {
  readonly lumberListGuid: string;
  readonly plateListGuid: string;
  readonly hangerListGuid: string;
}

export interface ParsedComponentDesign {
  readonly componentDesign: ComponentDesign;
  readonly loadSet?: LoadSet;
  readonly loadSettings?: LoadSettings;
  readonly analysisSettings?: AnalysisSettings;
}

export interface ParseFilesResult {
  readonly parsedComponentDesigns: readonly ParsedComponentDesign[];
  readonly errors: readonly string[];
  readonly inferredGroupName: string | null;
}

export interface UploadFilesResult {
  readonly componentDesignsWithTimestamps: readonly ComponentDesignWithTimestamps[];
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
  readonly quantitiesFromFiles: { [componentDesignGuid: string]: number };
  readonly protoJackGirderConnections: readonly ProtoJackGirderConnection[];
  readonly inferredGroupName: string | null;
}

export interface ProtoJackGirderConnection {
  readonly girderGuid: string;
  readonly jackName: string;
  readonly bearingLocationX: number;
  readonly bearingLocationY: number;
  readonly memberType: MemberType;
  readonly chordToUseWhenMultipleChoices: 'Outer' | 'Inner';
  readonly xLocation: number;
  readonly minimumAngleBetweenGirderAndJack: number;
  readonly numberOfJacks: number;
  readonly spacingOfJacks: number;
}

export type ConvertibleComponentFileExtension =
  'tre' | 'trs' | 'steelTrs' | 'trussDAndE' | 'tps' | 'pcd' | 'cyb' | 'bvn' | 'omn' | 'blade' | 'ifc';

export interface DownloadRequest {
  readonly guidType: 'ComponentDesign' | 'AnalysisSet';
  readonly guidToQuantity: {
    readonly [guid: string]: number;
  };
  readonly groupName: string | null;
  readonly sharedComponentSetLookupCode: string | null;
}

export interface DownloadTrussDAndERequest extends DownloadRequest {
  /** The project each guid in `guidToQuantity` belongs to, for the project data written into the file. */
  readonly guidToProjectGuid?: {
    readonly [guid: string]: string;
  };
}

export interface DownloadMaterialReportRequest extends DownloadRequest {
  readonly overLengthPrice: number;
}

export type DownloadMachineryTrsMemberType = 'All' | 'Chords' | 'Webs' | 'Blocks';
export interface DownloadMachineryTrsRequest extends DownloadRequest {
  readonly memberType: DownloadMachineryTrsMemberType;

  readonly maxMemberLengthInInches: number | null;
  readonly includeMembersLongerThanMaxLengthInSeparateFile: boolean | null;
}

/**
 * Like `ShopDrawingOptions`, but the properties which have counterparts in organization settings are optional; any
 * that are not provided default to the requesting user's organization settings.
 */
export interface ShopDrawingOptionsRequest extends Omit<
  ShopDrawingOptions,
  'portraitOrientation' | 'distanceFormat' | 'squareCutAngleIsZero'
> {
  readonly portraitOrientation: boolean | null;
  readonly distanceFormat: DistanceFormat | null;
  readonly squareCutAngleIsZero: boolean | null;
}

export interface DownloadShopDrawingsRequest extends DownloadRequest {
  readonly options: ShopDrawingOptionsRequest;
  readonly allowEmail: boolean;
}

/** The parts of a `DownloadTrussDesignDrawingsRequest` which do not depend on what is being drawn. */
export interface TrussDesignDrawingOptions {
  readonly templateId: number | null;
  readonly overflowTemplateId: number | null;
  readonly useRepairTemplate: boolean;
  readonly mergeIntoOnePdf: boolean;
  readonly ignoreErrors: boolean;
  readonly forceDarkMode: boolean;
  /** Also include a snapshot of `projectGuid`'s hierarchy and layout in that Shared Component Set. */
  readonly shareLayout: boolean;
}

export const DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS: TrussDesignDrawingOptions = {
  templateId: null,
  overflowTemplateId: null,
  useRepairTemplate: false,
  mergeIntoOnePdf: false,
  ignoreErrors: false,
  forceDarkMode: false,
  shareLayout: true,
};

export interface DownloadTrussDesignDrawingsRequest extends DownloadRequest, TrussDesignDrawingOptions {
  /** The job name printed on each component's drawing. Any component missing from this is printed with `groupName`. */
  readonly guidToJobName?: {
    readonly [guid: string]: string;
  };
  /**
   * The project the components are being drawn for, recorded on the Shared Component Set the drawings' QR codes link
   * to. Ignored if `sharedComponentSetLookupCode` is provided.
   */
  readonly projectGuid: string | null;
}

export interface ComparePlateListsRequest {
  readonly projectName: string;
  readonly componentDesignGuids: readonly string[];
  readonly plateListGuids: readonly string[];
}

export interface ProfileTrussSettings extends TrussSettingsBase {
  readonly definitionType: 'Profile';
  readonly profile: Profile;

  // The following properties exist on the ProfileTrussSettings instead of the Profile because they are only used while
  // the profile is being constructed. Once the profile is constructed they are redundant because the span and seat cuts
  // can be calculated from the profile.

  readonly span: number;
  readonly leftSeatCut: number;
  readonly rightSeatCut: number;
}

export type TrussSettings = ProfileTrussSettings | FeaturePatternTrussSettings;

export type PlateManufacturer =
  'MiTek' | 'Eagle' | 'Alpine' | 'Simpson' | 'CH Machine' | 'Cherokee Metal' | 'Any' | 'Combo';

export interface DesignInsertion {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotation: number;
}

export interface ComponentDesignUploadFile {
  readonly contents: string;
  readonly filename: string; // Should include extension
}

export interface ComponentDesignCreationInput {
  readonly jobId: number;
  readonly organization: string;
  readonly treUrl?: string;
  readonly ansUrl?: string;
  readonly treFile?: string;
  readonly ansFile?: string;
  readonly treFileName: string;
  readonly ansFileName: string;
  readonly plateManufacturer: PlateManufacturer;
  readonly insertions?: readonly DesignInsertion[];
  readonly convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo: boolean;
}

export interface GenerateLoadCasesResult {
  readonly componentDesign: ComponentDesign;
  readonly loadSet: LoadSet;
  readonly excavation: Excavation | null;
}

export interface ComponentDesignWithRecordedCanaries {
  readonly componentDesign: ComponentDesign;
  readonly excavation: Excavation | null;
}

export type StubDistanceOption = 'DistanceToStub' | 'RemainingSpan';

export interface CameraSettings {
  readonly flipped: boolean;
  readonly reversed: boolean;
}

export type DimensionPointCategory = 'General' | 'Detailed' | 'Minimal';
export type DimensionLabelType = 'Separated' | 'Cumulative' | 'Combined';

export interface DimensionSettings {
  readonly showGrid: boolean;
  readonly showStandardDimensions: boolean;
  readonly dimensionLabelType: DimensionLabelType;
  readonly visibleDimensionCategory: DimensionPointCategory;
  readonly showPitch: boolean;
  readonly showSelectedMemberCutAngles: boolean;
  readonly showSelectedMemberLengthDimensions: boolean;
}

export interface SnapSettings {
  readonly showCentroidSnaps: boolean;
  readonly showIntersectionSnaps: boolean;
  readonly showMidLineSnaps: boolean;
  readonly showVertexSnaps: boolean;
}

export const COLOR_BY_OPTIONS = ['None', 'LumberProperties', 'Combined', 'LumberGeometry'] as const;
export type ColorByOption = (typeof COLOR_BY_OPTIONS)[number];

export function isColorByOption(colorBy: string): colorBy is ColorByOption {
  return (COLOR_BY_OPTIONS as readonly string[]).includes(colorBy);
}

export interface PlateDetailSettings {
  readonly showPlateDetails: boolean;
  readonly hideForMostCommonType: boolean;
  readonly hideForMostCommonSize: boolean;
}

export type MemberLabelContent = 'MemberName' | 'AssemblyName' | 'OverallLength' | 'Hidden';

export interface MemberLabelOptions {
  nonWebLabelContent: MemberLabelContent;
  webLabelContent: MemberLabelContent;
}

export interface ViewOptionSettings {
  readonly colorBy: ColorByOption;
  readonly showPlates: boolean;
  readonly showPlateNames: boolean;
  readonly plateDetailSettings: PlateDetailSettings;
  readonly memberLabelOptions: MemberLabelOptions;
  readonly snapSettings: SnapSettings;
  readonly distanceFormat: DistanceFormat;
  readonly highVisibilityMode: boolean;
}

export type DistanceFormat = 'Architectural' | 'FeetInchesSixteenths';

export type BevelCutTypePreference = 'PreferAny' | 'PreferDouble' | 'PreferNone';

export const DEFAULT_BEVEL_CUT_TYPE_PREFERENCE: BevelCutTypePreference = 'PreferDouble';

export interface LayoutSettings {
  readonly bevelCutTypePreference: BevelCutTypePreference;
}

export interface UserSettings {
  readonly cameraSettings: CameraSettings;
  readonly dimensionSettings: DimensionSettings;
  readonly viewOptionSettings: ViewOptionSettings;
  readonly layoutSettings: LayoutSettings;
}

export interface ComponentDesignQuantities {
  readonly [guid: string]: number;
}

export type CutSheetMemberType = 'Chord' | 'Web' | 'Block' | 'Other';

export interface ShopDrawingOptions {
  readonly includeComponentSchematics: boolean;
  readonly includeCutSheets: boolean;
  readonly combineCutSheets: boolean;
  readonly portraitOrientation: boolean;
  readonly distanceFormat: DistanceFormat;
  readonly squareCutAngleIsZero: boolean;
  readonly cutsOnSamePage: boolean;
  readonly includedMemberTypes: readonly CutSheetMemberType[];
}

export const NumericalFilterTypes = ['<', '<=', '=', '>=', '>'] as const;
export type NumericalFilterType = (typeof NumericalFilterTypes)[number];

export interface DistanceFilter {
  readonly type: NumericalFilterType;
  readonly value: number;
}

export interface LumberFilter {
  readonly memberType?: MemberType;
  readonly species?: LumberSpecies;
  readonly grade?: LumberGrade;
  readonly nominalThickness?: string;
  readonly nominalWidth?: string;
  readonly treatmentType?: LumberTreatmentType;
  readonly length?: DistanceFilter;
}

export interface ReplaceLumberRequest {
  readonly componentDesign: ComponentDesign;
  readonly filter: LumberFilter;
  readonly newLumber: Lumber;
}

// http://jsonpatch.com/
export type JsonPatch =
  | {
      readonly op: 'add';
      readonly path: string;
      readonly value: any;
    }
  | {
      readonly op: 'remove';
      readonly path: string;
    }
  | {
      readonly op: 'replace';
      readonly path: string;
      readonly value: any;
    }
  | {
      readonly op: 'move';
      readonly path: string;
      readonly from: string;
    }
  | {
      readonly op: 'copy';
      readonly path: string;
      readonly from: string;
    }
  | {
      readonly op: 'test';
      readonly path: string;
      readonly value: any;
    };

export interface MaxDeflectionEntry {
  readonly loading: DeflectionLoading;
  readonly direction: string;
  readonly location: string;
  readonly greatestDeflection: number;
}

export interface HorizontalMaxDeflectionEntry extends MaxDeflectionEntry {}

export interface VerticalMaxDeflectionEntry extends MaxDeflectionEntry {
  readonly deflectionRatio: number | null;
  readonly deflectionLimit: number;
}

export function isVerticalMaxDeflectionEntry(entry: MaxDeflectionEntry): entry is VerticalMaxDeflectionEntry {
  const verticalEntry = entry as VerticalMaxDeflectionEntry;
  return verticalEntry.deflectionLimit != null;
}

export interface MaxDeflectionTable {
  readonly totalVerticalMaxDeflection: VerticalMaxDeflectionEntry | null;
  readonly liveVerticalMaxDeflection: VerticalMaxDeflectionEntry | null;
  readonly totalHorizontalMaxDeflection: HorizontalMaxDeflectionEntry;
}

export interface ComponentDesignToProjectAndQuantityMap {
  readonly [componentDesignGuid: string]: {
    readonly projectGuid: string;
    readonly quantity: number;
  };
}

export interface AnalysisOptions {
  readonly commitName?: string;
  readonly abbreviated?: boolean;
  readonly logVariables?: boolean;
}

export interface StationComponentDesign {
  readonly index: number | null;
  readonly quantity: number | null;
}

export interface Station {
  readonly guid: string;
  readonly organization: string;
  readonly name: string;
  readonly followsSchedule: boolean;
  readonly role: StationRole;
  readonly currentProductionComponentInstanceGuid: string | null;
  readonly componentDesigns: { [guid: string]: StationComponentDesign };
}

export const STATION_ROLES = ['Assembly', 'ChordSaw', 'WebSaw', 'BlockSaw', 'PlatePull', 'Bundler'] as const;
export type StationRole = (typeof STATION_ROLES)[number];

export interface SynchronizedResult<T> {
  readonly result: T;
  readonly successfullySynchronized: boolean;
}

export interface MaxAllowableLoads {
  readonly uplift1_60: number;
  readonly download1_00: number;
  readonly download1_15: number;
  readonly download1_25: number;
}

export interface ConnectionHardwareConfiguration {
  readonly manufacturer: string;
  readonly modelName: string;

  readonly configurationGuid: string;

  readonly customFields: Record<string, string>;
  readonly fastenerSchedule: Record<string, string>;

  readonly maxAllowableLoads: MaxAllowableLoads;
}

export type CanaryMetadata = { [key: string]: string };

export interface CanaryPostMortem {
  readonly causeOfDeath: string;
  readonly componentDesignGuid: string | null;
  readonly metadata: CanaryMetadata;
}

export interface Excavation {
  readonly componentDesignGuid: string;
  readonly metadata: { readonly [key: string]: string };
  readonly reportedToxins: readonly string[];
}

export interface JackConnectionsOnGirderWithNames {
  readonly girderName: string;
  readonly connections: readonly JackConnectionWithNames[];
}

export interface JackConnectionWithNames {
  readonly jackName: string;
  readonly girderXLocation: number;
  readonly girderChordType: ChordType;
  readonly jackXLocation: number;
  readonly jackChordType: ChordType;
  readonly minimumAngleBetweenGirderAndJack: number;
}

export interface JackConnectionsOnGirderWithGuids {
  readonly girderGuid: string;
  readonly connections: readonly JackConnectionWithGuids[];
}

export interface JackConnectionWithGuids {
  readonly jackGuid: string;
  readonly girderXLocation: number;
  readonly girderChordType: ChordType;
  readonly jackXLocation: number;
  readonly jackChordType: ChordType;
  readonly minimumAngleBetweenGirderAndJack: number;
}

export const LUMBER_SPECIES = [
  'Southern_Pine',
  'Mixed_Southern_Pine',
  'Alaska_Cedar',
  'Alaska_Hemlock',
  'Alaska_Spruce',
  'Alaska_Yellow_Cedar',
  'Aspen',
  'Bald_Cypress',
  'Beech_Birch_Hickory',
  'Coast_Sitka_Spruce',
  'Cottonwood',
  'Douglas_Fir_Larch',
  'Douglas_Fir_Larch_North',
  'Douglas_Fir_South',
  'Eastern_Hemlock_Balsam_Fir',
  'Eastern_Hemlock_Tamarack',
  'Eastern_Softwoods',
  'Eastern_White_Pine',
  'Engelmann_Spruce_Lodgepole_Pine',
  'Hem_Fir',
  'Hem_Fir_North',
  'Mixed_Maple',
  'Mixed_Oak',
  'Northern_Red_Oak',
  'Northern_Species',
  'Northern_White_Cedar',
  'Norway_Spruce_North',
  'Ponderosa_Pine',
  'Red_Maple',
  'Red_Oak',
  'Redwood',
  'Spruce_Pine_Fir',
  'Spruce_Pine_Fir_South',
  'Western_Cedars',
  'Western_Juniper',
  'Western_Woods',
  'White_Oak',
  'White_Fir',
  'Yellow_Cedar',
  'Yellow_Poplar',
  // Structural Composites
  'MasterPlank_LVL',
  'RigidLam_LVL',
  'Parallam_PSL',
  'VersaLam_LVL',
  'GPLam_LVL',
  'LP_LVL',
  'BC_OSB',
  'LP_SolidStart_LSL',
  'KSLVL',
  'Unknown',
  'Combo',
] as const;
export type LumberSpecies = (typeof LUMBER_SPECIES)[number];

export const LUMBER_GRADES = [
  // Visual Grades
  'Dense_Select_Structural',
  'Select_Structural',
  'Select_Structural_Open_Grain',
  'Select_And_Better',
  'Non_Dense_Select_Structural',
  'Dense_Number_1',
  'Dense_Number_2',
  'Dense_Number_3',
  'Number_1_And_Better',
  'Number_2_And_Better',
  'Number_1',
  'Number_2',
  'Number_3',
  'Number_1_Open_Grain',
  'Number_2_Open_Grain',
  'Number_3_Open_Grain',
  'Non_Dense_Number_1',
  'Non_Dense_Number_2',
  'Non_Dense_Number_3',
  'Number_1_And_2',
  'Stud',
  'Construction',
  'Standard',
  'Utility',
  'Clear_Structural',
  'Economy',

  // Structural Composite LFL
  'LFL_1_6E',
  'LFL_1_7E',
  'LFL_1_9E',
  'LFL_2_1E',

  // Structural Composite LVL
  'LVL_1_3E',
  'LVL_1_5E',
  'LVL_1_8E',
  'LVL_2_0E',
  'LVL_2_2E',
  'LVL_2_3E',

  // Structural Composite LSL
  'LSL_1730F_1_35E',
  'LSL_2360F_1_55E',
  'LSL_2500F_1_75E',

  // Machine Stress Rated
  'MSR_750f_1_4E',
  'MSR_850f_1_4E',
  'MSR_900f_1_0E',
  'MSR_975f_1_6E',
  'MSR_1050f_1_2E',
  'MSR_1050f_1_6E',
  'MSR_1200f_1_2E',
  'MSR_1200f_1_3E',
  'MSR_1200f_1_6E',
  'MSR_1250f_1_4E',
  'MSR_1250f_1_6E',
  'MSR_1350f_1_3E',
  'MSR_1350f_1_4E',
  'MSR_1400f_1_2E',
  'MSR_1450f_1_3E',
  'MSR_1450f_1_5E',
  'MSR_1500f_1_4E',
  'MSR_1500f_1_5E',
  'MSR_1500f_1_6E',
  'MSR_1500f_1_7E',
  'MSR_1600f_1_4E',
  'MSR_1650f_1_3E',
  'MSR_1650f_1_5E',
  'MSR_1650f_1_6E',
  'MSR_1650f_1_7E',
  'MSR_1700f_1_6E',
  'MSR_1800f_1_5E',
  'MSR_1800f_1_6E',
  'MSR_1800f_1_8E',
  'MSR_1800f_2_0E',
  'MSR_1850f_1_7E',
  'MSR_1950f_1_5E',
  'MSR_1950f_1_7E',
  'MSR_2000f_1_6E',
  'MSR_2100f_1_8E',
  'MSR_2250f_1_7E',
  'MSR_2250f_1_8E',
  'MSR_2250f_1_9E',
  'MSR_2400f_1_8E',
  'MSR_2400f_2_0E',
  'MSR_2500f_2_2E',
  'MSR_2550f_1_8E',
  'MSR_2550f_2_1E',
  'MSR_2700f_2_0E',
  'MSR_2700f_2_2E',
  'MSR_2850f_1_8E',
  'MSR_2850f_2_3E',
  'MSR_3000f_2_4E',

  // Machine Evaluated Lumber
  'MEL_M_5',
  'MEL_M_6',
  'MEL_M_7',
  'MEL_M_8',
  'MEL_M_9',
  'MEL_M_10',
  'MEL_M_11',
  'MEL_M_12',
  'MEL_M_13',
  'MEL_M_14',
  'MEL_M_15',
  'MEL_M_16',
  'MEL_M_17',
  'MEL_M_18',
  'MEL_M_19',
  'MEL_M_20',
  'MEL_M_21',
  'MEL_M_22',
  'MEL_M_23',
  'MEL_M_24',
  'MEL_M_25',
  'MEL_M_26',
  'MEL_M_27',
  'MEL_M_28',
  'MEL_M_29',
  'MEL_M_30',
  'MEL_M_31',
  'MEL_M_32',
  'MEL_M_33',
  'MEL_M_34',
  'MEL_M_35',
  'MEL_M_36',
  'MEL_M_37',
  'MEL_M_38',
  'MEL_M_39',
  'MEL_M_40',
  'MEL_M_41',
  'MEL_M_42',

  'MasterPlank',

  'Kerto_S_2_0E',

  'Unknown',

  'Combo',
] as const;
export type LumberGrade = (typeof LUMBER_GRADES)[number];

export const LUMBER_TREATMENT_TYPES = [
  'Untreated',

  // Fire treatment
  'FireRetardantUnknown',
  'FirePro',
  'Dricon',
  'PyroGuard',
  'DBlaze',

  // Other treatment
  'MCA',
  'MCQ',
  'Preservative',
  'IncisedPreservative',
  'ACQ',
  'Borate',
] as const;
export type LumberTreatmentType = (typeof LUMBER_TREATMENT_TYPES)[number];

export type StockLengthsDefinition =
  { readonly start: number; readonly end: number; readonly increment: number } | readonly number[];

export interface LumberSet {
  readonly guid: string;

  readonly species: readonly LumberSpecies[];
  readonly comboSpecies: readonly (readonly LumberSpecies[])[];
  readonly grades: readonly LumberGrade[];
  readonly comboGrades: readonly (readonly LumberGrade[])[];
  readonly treatmentTypes: readonly LumberTreatmentType[];
  readonly actualDimensions1: readonly number[];
  readonly actualDimensions2: readonly number[];
  readonly stockLengths: StockLengthsDefinition;
}

export interface OrganizationMaterialSetEditResponse {
  result: OrganizationMaterialSet;
  prototype: OrganizationMaterialSetPrototype;
}

export interface Line {
  readonly guid: string;
  readonly parentGuid?: string;

  readonly organization: string;
  readonly name: string;
}

export interface LineOrDirectoryPatch {
  name?: string;

  // A null value in a patch signifies that the parentGuid should be removed, as opposed to `undefined`, which would
  //   simply indicate that the parentGuid is not affected by this patch.
  parentGuid?: string | null;
}

export interface StationInLineInsertion {
  readonly guid: string;
  readonly xIndex: number;
  readonly yIndex: number;
}

export interface LineWithEagerlyLoadedStations {
  readonly line: Line;
  readonly stations: readonly StationInLineInsertion[];
}

export interface LineDirectory {
  readonly guid: string;
  readonly parentGuid?: string;

  readonly organization: string;
  readonly name: string;
}

export interface ProductionGroup {
  readonly guid: string;
  readonly projectGuid: string;
  readonly organization: string;
  readonly projectId: string;
  readonly projectShortName: string;
  readonly buildingType: string;
  readonly buildingInstance: string;
  readonly level: string;
}

export interface BatchWithoutId {
  readonly guid: string;
  readonly productionGroupGuid: string;
}

export interface Batch extends BatchWithoutId {
  readonly id: number;
}

export interface BundleWithoutId {
  readonly guid: string;
  readonly productionGroupGuid: string;
  readonly scheduleGroupGuid: string | null;
  readonly scheduleLineGroupGuid: string | null;
  readonly scheduleLineGroupPriority: number | null;
  readonly truckloadGuid: string | null;
}

export interface Bundle extends BundleWithoutId {
  readonly id: number;
}

export type ProductionComponentInstanceStatus =
  'Unscheduled' | 'Scheduled' | 'StartedAssembly' | 'FinishedAssembly' | 'StoredOnYard' | 'Shipped' | 'Delivered';

export interface ProductionComponentInstance {
  readonly guid: string;
  readonly analysisSetGuid: string;
  readonly productionGroupGuid: string;
  readonly batchGuid: string | null;
  readonly bundleGuid: string | null;
  readonly bundlePositionX: number | null;
  readonly bundlePositionY: number | null;
  readonly rebuildOf: string | null;
  readonly status: ProductionComponentInstanceStatus;
}

export interface ProductionComponentInstanceBuildLog {
  readonly instanceGuid: string;
  readonly stationGuid: string;
  readonly startProduction: string;
  readonly endProduction: string | null;
}

export interface ScheduleGroup {
  readonly guid: string;
  readonly name: string;
  readonly organization: string;

  /**
   * This is a DateOnly on the server. It will have the format 'YYYY-MM-DD'.
   * TODO Use Temporal.PlainDate here when it is widely available
   */
  readonly targetDate: string | null;
}

export interface ScheduleLineGroup {
  readonly guid: string;
  readonly name: string;
  readonly organization: string;
  readonly lineGuid: string;
  readonly priority: number;

  /**
   * This is a DateTime on the server. It will have the format 'YYYY-MM-DDTHH:MM:SS'.
   */
  readonly scheduledDate: string | null;
}

export interface ScheduleLineGroupData {
  readonly scheduleLineGroup: ScheduleLineGroup;
  readonly orderedProductionComponentInstancesByBundleGuid: {
    [bundleGuid: string]: readonly ProductionComponentInstance[];
  };
  readonly productionGroupsByGuid: { [productionGroupGuid: string]: ProductionGroup };
  readonly batchesByGuid: { [batchGuid: string]: Batch };
  readonly orderedBundles: readonly Bundle[];
}

export interface Truckload {
  readonly guid: string;
  readonly id: number;
  readonly organization: string;
  readonly trailerGuid: string | null;
  readonly truckDriverGuid: string | null;
  readonly dateShipped: string | null;
  readonly miscellaneousItems: readonly MiscellaneousItem[];
}

export interface MiscellaneousItem {
  readonly guid: string;
  readonly item: string;
  readonly quantity: string;
  readonly notes: string | null;
}

/**
 * Not persisted, used for display
 */
export interface GroupedProductionComponentInstance {
  /** Used as React key in nav panel */
  readonly guid: string;

  readonly productionComponentInstanceGuids: readonly string[];
  readonly analysisSetGuid: string;
  readonly quantity: number;

  readonly trussName: string;
  readonly bundleIds: string | null;
}

export interface Trailer {
  readonly guid: string;
  readonly name: string;
  readonly organization: string;
  readonly archived: boolean;
}

export interface TruckDriver {
  readonly guid: string;
  readonly name: string;
  readonly organization: string;
  readonly archived: boolean;
}

export type ProductionOutputType =
  | 'BundleLabels'
  | 'ComponentWrapLabels'
  | 'FloorChordPullList'
  | 'WebAndBlockCutSheets'
  | 'WipLabels'
  | 'ProductionAndShippingList'
  | 'CartLabels'
  | 'ComponentReport'
  | 'LumberPickList'
  | 'PlatePickList'
  | 'TpsFiles'
  | 'TreFiles'
  | 'TrsMachineryFiles';

export type ProductionArtifactsContext = 'ProductionGroup' | 'ScheduleGroup' | 'ScheduleLineGroup';

export interface ProductionOutputParameters {
  readonly componentLabelCountPerComponent: number;
}

export interface ProductionOutputPreset {
  readonly guid: string;
  readonly organization: string;
  readonly name: string;
  readonly outputTypes: readonly ProductionOutputType[];
  readonly parameters: ProductionOutputParameters;
}

export interface ProjectLayout {
  readonly projectGuid: string;
  readonly roofContainers: Record<string, RoofContainer>;
  readonly featurePatternContainers: Record<string, FeaturePatternTrussContainer>;
  readonly trussEnvelopes: Record<string, TrussEnvelope>;
  readonly roofPlanes: Record<string, RoofPlane>;
  readonly ceilingPlanes: Record<string, CeilingPlane>;
  readonly ceilingTrays: Record<string, CeilingTray>;
  readonly bearingEnvelopes: Record<string, BearingEnvelope>;
  readonly layoutLoads: Record<string, never>;
  readonly workLines: Record<string, WorkLine>;
  readonly userDistanceDimensions: readonly LayoutUserDistanceDimension[];
  readonly snappingUnderlays: Record<string, SnappingUnderlay>;
}

export type EnvelopeJustification = 'Back' | 'Center' | 'Front';

export interface TrussEnvelopeBevelCut {
  type: TrussEnvelopeBevelCutType;
  angle: number;
}

export type TrussEnvelopeBevelCutType = 'Back' | 'Double' | 'Front';

export function flipJustification<T extends EnvelopeJustification | TrussEnvelopeBevelCutType>(type: T) {
  return type === 'Back' ? 'Front' : type === 'Front' ? 'Back' : type;
}

export const LABEL_PLACEMENTS = ['Left', 'CenterLeft', 'Center', 'CenterRight', 'Right'] as const;

/**
 * Where a truss envelope's name label is drawn along the envelope, in the layout app and the PDF/DXF outputs.
 * `Left`/`Right` refer to the truss's design left/right ends (`leftPoint`/`rightPoint`);
 * `CenterLeft`/`CenterRight` sit just to one side of the midpoint so the label clears the ridge line.
 */
export type EnvelopeLabelPlacement = (typeof LABEL_PLACEMENTS)[number];

/**
 * Mirrors a label placement across the envelope's midpoint, so a label keeps its physical position when the truss's
 * ends are swapped (e.g. by `flipTrussEnvelope`). `Center` is unchanged.
 */
export function flipLabelPlacement(placement: EnvelopeLabelPlacement): EnvelopeLabelPlacement {
  switch (placement) {
    case 'Left':
      return 'Right';
    case 'Right':
      return 'Left';
    case 'CenterLeft':
      return 'CenterRight';
    case 'CenterRight':
      return 'CenterLeft';
    case 'Center':
      return 'Center';
    default:
      return assertExhaustiveSwitch(placement);
  }
}

export type ContainerLevel = 'BaseLevel' | 'CapLevelOne' | 'CapLevelTwo' | 'NotCapped';

export type TopOrBottomCutType = 'Absolute' | 'RoofPlane' | 'CeilingPlane';

export interface TopOrBottomCutBase {
  readonly type: TopOrBottomCutType;
}

export interface AbsoluteTopOrBottomCut extends TopOrBottomCutBase {
  readonly type: 'Absolute';
  readonly plane: Plane3D;
}

export interface RoofPlaneTopOrBottomCut extends TopOrBottomCutBase {
  readonly type: 'RoofPlane';
  readonly roofPlaneGuid: string;
}

export interface CeilingPlaneTopOrBottomCut extends TopOrBottomCutBase {
  readonly type: 'CeilingPlane';
  readonly ceilingPlaneGuid: string;
}

export type TopOrBottomCut = AbsoluteTopOrBottomCut | RoofPlaneTopOrBottomCut | CeilingPlaneTopOrBottomCut;

export interface OrderedCuts {
  readonly cuts: readonly TopOrBottomCut[];
  readonly reversed: boolean;
}

export const DEFAULT_ORDERED_CUTS: OrderedCuts = Object.freeze<OrderedCuts>({ cuts: [], reversed: false });

export type EnvelopeEndConnectionType = 'None' | 'TopChord' | 'BottomChord' | 'TopAndBottomChords';

export interface TrussEnvelope {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly name: string;

  readonly componentType: TrussType;
  readonly definitionType: TrussDefinitionType;

  readonly roofContainerGuid: string | null;
  readonly featurePatternContainerGuid: string | null;

  readonly leftPoint: Point2D;
  readonly rightPoint: Point2D;
  readonly elevation: number;
  readonly justification: EnvelopeJustification;
  readonly thickness: number;
  readonly numberOfPlies: number;
  readonly leftOverhang: number;
  readonly rightOverhang: number;
  readonly leftBevelCut: TrussEnvelopeBevelCut | null;
  readonly rightBevelCut: TrussEnvelopeBevelCut | null;
  readonly leftEndConnectionOverride: EnvelopeEndConnectionType | null;
  readonly rightEndConnectionOverride: EnvelopeEndConnectionType | null;
  readonly componentDesignGuid: string | null;
  readonly topCuts: readonly TopOrBottomCut[];
  readonly bottomCuts: readonly TopOrBottomCut[];
  readonly orderedTopCuts: OrderedCuts;
  readonly orderedBottomCuts: OrderedCuts;
  readonly containerLevel: ContainerLevel;
  readonly capLevelOne: number;
  readonly capLevelTwo: number;

  readonly featurePatternSettings: FeaturePatternTrussEnvelopeSettings | null;

  readonly labelPlacement: EnvelopeLabelPlacement;
}

export interface CalculationError {
  readonly type:
    | 'NullContainerSolid'
    | 'ZedgeEnvelope'
    | 'VerticalTerminalOrderedCuts'
    | 'OrderedCutsParallelToFace'
    | 'ConsecutiveParallelOrderedCuts'
    | 'NullCutFacet'
    | 'DisorientedOrderedCuts'
    | 'BoundaryCornerFailure'
    | 'BoundaryFailure'
    | 'NullTrussPolygon'
    | 'MissingDependencies'
    | 'CalculationPanic';
  readonly boundarySide: 'Top' | 'Bottom' | null;
  readonly boundaryType: 'Inferred' | 'OrderedCuts' | null;
  readonly trussSide: 'Left' | 'Right' | 'LeftAndRight' | null;
}

export type FeaturePatternInfoErrorType = 'SlopeAndStepsUnsupported' | 'ProfileUnsupported' | 'NoBearings';

export type CreateTrussError =
  | { type: 'TrussSolid'; readonly error: CalculationError }
  | { type: 'MemberGeneration'; readonly isProfileTruss: boolean }
  | { type: 'MissingJacks'; readonly missingJackEnvelopeNames: readonly string[] }
  | { type: 'FeaturePatternInfo'; readonly error: FeaturePatternInfoErrorType }
  | { type: 'Unexpected'; readonly userVisibleExceptionMessage: string | null };

export type BearingEnvelopeType = 'Wall' | 'Beam';
export type BeamOrientation = 'Drop' | 'Flush';

export interface BearingEnvelope {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly name: string;
  readonly leftPoint: Point2D;
  readonly rightPoint: Point2D;
  readonly thickness: number;
  readonly numberOfPlies: number;
  readonly bearingType: BearingEnvelopeType;
  readonly beamOrientation: BeamOrientation;
  readonly top: number;
  readonly bottom: number;
  readonly justification: EnvelopeJustification;
  readonly nonStructural: boolean;
}

/** The only support we offer for `LayoutLoad`s is round-tripping without loss. */
export type LayoutLoad = never;

export interface SnappingUnderlay {
  readonly guid: string;
  readonly name: string;
  readonly extractedPaths: readonly (readonly Point2D[])[];
  readonly scale: number;
  readonly translate: Point2D;
  readonly rotation: number;
}

export interface BearingExtractionResult {
  readonly bearingEnvelopes: BearingEnvelope[];
  readonly roofPlanes: RoofPlane[];
}

export interface SolidDataFace {
  /**
   * Each element is an index into SolidData.Vertices for that face's outer bounds.
   */
  readonly outer: readonly number[];
  /**
   * Each element is a list of vertex indices indexing into SolidData.Vertices for that face's inner bounds.
   */
  readonly inner: readonly (readonly number[])[];
}

export interface SolidData {
  readonly vertices: readonly Point3D[];
  readonly faces: readonly SolidDataFace[];
}

export interface RoofContainer {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly name: string;

  readonly solidData: SolidData | null;
  readonly overhangSolidDataList: readonly (SolidData | null)[] | null;
}

export type WorkLineScope =
  | { readonly type: 'Project' }
  | { readonly type: 'Building'; readonly buildingGuid: string }
  | { readonly type: 'Level'; readonly levelGuid: string };

export interface WorkLineBase {
  guid: string;
  name: string;

  bounding: boolean;
  scope: WorkLineScope;
}

export type WorkLine = WorkLineBase &
  ({ geometryType: 'Line'; geometry: Line2D } | { geometryType: 'Segment'; geometry: Segment2D });

export interface RoofPlaneBearingEnvelopeGeometry {
  readonly type: 'BearingEnvelope';
  readonly bearingEnvelopeGuid: string;
}

export interface RoofPlaneAbsoluteGeometry {
  readonly type: 'Absolute';
  readonly elevation: number;
  readonly segment: Segment2D;
  readonly isInfinite: boolean;
}

export type RoofPlaneGeometry = RoofPlaneBearingEnvelopeGeometry | RoofPlaneAbsoluteGeometry;

export interface AbsoluteVerticalCut {
  readonly type: 'AbsoluteVertical';
  readonly segment: Segment2D;
  readonly isInfinite: boolean;
}

export type AbsoluteVerticalPlane = Omit<AbsoluteVerticalCut, 'type'>;

export interface AgainstPlaneCut {
  readonly type: 'AgainstPlane';
  readonly cuttingPlaneGuid: string;
}

export type PlaneCut = AbsoluteVerticalCut | AgainstPlaneCut;

export interface RoofPlane {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly flipped: boolean;

  readonly geometry: RoofPlaneGeometry;

  readonly slopeInDegrees: number;
  readonly heelSettings: HeelSettings;
  readonly deckingThickness: number;
  readonly topChordWidth: number;
  readonly overhang: number;
  readonly subfasciaThickness: number;
  readonly cantilever: number;
  readonly cuts: readonly PlaneCut[];
}

export interface CeilingPlane {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly flipped: boolean;

  readonly geometry: RoofPlaneGeometry;

  readonly slopeInDegrees: number;
  readonly cuts: readonly PlaneCut[];
}

export interface EstimateData {
  readonly plateGaugeToTotalPounds: Record<string, number>;
  readonly lumberToTotalBoardFeet: Record<string, number>;
}

interface LumberBidEntry {
  readonly lumber: string;
  readonly boardFeet: number;
  readonly costPerThousandBoardFeet: string;
  readonly cost: string;
}

interface PlateBidEntry {
  readonly plateGauge: number;
  readonly pounds: number;
  readonly costPerPound: string;
  readonly cost: string;
}

export interface BidData {
  readonly projectName: string;
  readonly lumber: readonly LumberBidEntry[];
  readonly lumberTotal: string;
  readonly plates: readonly PlateBidEntry[];
  readonly platesTotal: string;
  readonly laborCostPerBoardFoot: string;
  readonly totalBoardFeet: number;
  readonly laborTotal: string;
  readonly freightCostPerMile: string;
  readonly numberOfTruckloads: number;
  readonly milesToBuildingSite: number;
  readonly freightTotal: string;
  readonly overhead: string;
  readonly totalBeforeMargin: string;
  readonly marginPercentage: number;
  readonly margin: string;
  readonly total: string;
}

export interface OrganizationSettings {
  readonly organization: string;
  readonly useDistanceFormat: DistanceFormat;
  /** If false, the square cut angle is 90 degrees. */
  readonly squareCutAngleIsZero: boolean;
  readonly portraitOrientationShopDrawings: boolean;
  /**
   * When true, downloaded files are named with the date and time of the download appended, e.g.
   * `Project Cut Sheet 2026-08-24 143005.pdf`.
   */
  readonly appendDateAndTimeToDownloadFileNames: boolean;
  readonly scarfCutThreshold: number;
}

export const DEFAULT_ORGANIZATION_SETTINGS: Omit<OrganizationSettings, 'organization'> = {
  useDistanceFormat: 'FeetInchesSixteenths',
  squareCutAngleIsZero: false,
  portraitOrientationShopDrawings: false,
  appendDateAndTimeToDownloadFileNames: false,
  scarfCutThreshold: 9,
};

/**
 * Things the requesting user's organization is allowed to do which are granted by organization rather than by user
 * role. The allow lists themselves live on the server, which enforces them; this is only so that clients offer what
 * the server will accept.
 */
export interface OrganizationCapabilities {
  readonly maySuppressAnalysisWarningsOnDrawings: boolean;
}

export interface CeilingTray {
  readonly guid: string;
  readonly levelGuid: string | null;

  readonly position: Point2D;

  readonly verticalAnchor: 'Upper' | 'Lower' | 'Center';
  readonly horizontalAnchor: 'Left' | 'Right' | 'Center';

  readonly width: number;
  readonly length: number;
  readonly rotationAngle: number;

  readonly bottomElevation: number;
  readonly topElevation: number;

  readonly sideAngle: number;
}

export interface EstimateInfo {
  readonly guid: string;
  readonly name: string;
  readonly created: string;
  readonly createdByEmail: string;
  readonly componentDesigns: { [componentDesignGuid: string]: number };
  readonly otherMaterials: readonly OtherMaterial[];
  readonly userSpecifiedLumberPrices: readonly LumberWithStockLengthAndPrice[];
  readonly userSpecifiedPlatePrices: { [plateType: string]: number };
  readonly userSpecifiedHangerPrices: { [connectorType: string]: number };
  readonly userSpecifiedCustomHangerPrices: { [connectorType: string]: number };
  readonly includeHangerPricing: boolean;
  readonly laborCostPerUnit: number;
  readonly laborCostUnit: 'LF' | 'BF';
  readonly freightCostPerMile: number;
  readonly numberOfMilesOneWay: number;
  readonly numberOfTruckloads: number;
  readonly overhead: number;
  readonly marginPercentage: number;
  readonly totalSquareFeet: number;
  readonly notes: string | null;
}

export interface ProjectEstimate extends EstimateInfo {
  readonly projectGuid: string;
  readonly projectSnapshotGuid: string;
  readonly projectFile: ProjectFileMetadata | null; // null for old estimates from before we tracked files
}

export interface OtherMaterial {
  readonly guid: string;
  readonly description: string;
  readonly cost: number;
}

export interface LumberWithStockLengthAndPrice {
  readonly lumber: Lumber;
  readonly stockLengthInInches: number;
  readonly price: number;
}

export interface EstimateMetrics {
  readonly numberOfDistinctMembers: number;
  readonly numberOfPlates: { [componentDesignGuid: string]: number };
  readonly feetByComponentDesignGuid: { [componentDesignGuid: string]: { boardFeet: number; linearFeet: number } };
  readonly componentDesignGuidToThickness: { [componentDesignGuid: string]: number };
}

export interface EstimateMetricsAndMissingPrices extends EstimateMetrics {
  readonly missingPlateTypePrices: readonly PlateType[];
  readonly missingHangerPrices: readonly StandardConnectingHardwareModel[];
  readonly missingCustomHangerPrices: readonly string[];
  readonly missingLumberPrices: readonly { readonly lumber: Lumber; readonly stockLengthInInches: number }[];
}

export interface LumberPrice {
  readonly guid: string;
  readonly organization: string;
  readonly location: string;
  readonly pricePerThousandBoardFeet: number;
  readonly nominalThicknessInInches: number;
  readonly nominalWidthInInches: number;
  readonly lengthInInches: number;
  readonly grade: LumberGrade;
  readonly species: LumberSpecies;
  readonly treatmentType: LumberTreatmentType;
  readonly priceDateTime: string;
  readonly notes: string | null;
}

export interface PlatePrice {
  readonly guid: string;
  readonly organization: string;
  readonly location: string;
  readonly pricePerPound: number;
  readonly plateType: PlateType;
  readonly priceDateTime: string;
  readonly notes: string | null;
}

export interface HangerPrice {
  readonly guid: string;
  readonly organization: string;
  readonly location: string;
  readonly price: number;
  readonly hanger: StandardConnectingHardwareModel;
  readonly priceDateTime: string;
  readonly notes: string | null;
}

export type DigitalQCOutputFormat = 'Sbca_0_4_2' | 'Tpi_1_0_0';

export interface AnalysisSetGuidWithQuantity {
  readonly analysisSetGuid: string;
  readonly quantity: number;
}

export interface SharedComponentSet {
  readonly lookupCode: string;
  readonly organization: string;
  readonly sourceProjectGuid: string | null;
  readonly projectSnapshotGuid: string | null;
  readonly name: string;
  readonly analysisSetGuidsWithQuantities: readonly AnalysisSetGuidWithQuantity[];
  readonly created: string;
  readonly contactEmail: string;
  readonly contactName: string;
  readonly contactPhone: string;
}

export interface AnalysisSetsWithInformation {
  readonly analysisSets: readonly AnalysisSet[];
  readonly history: readonly AnalysisHistoryRecord[];
  readonly timestamps: readonly ComponentDesignTimestamps[];
  readonly carriedComponentConnections: readonly JackGirderConnection[];
  readonly loadSets: readonly LoadSet[];
  readonly dimensionSets: readonly DimensionSet[];
  readonly componentDesignGuidsNoLongerInProject: readonly string[];
  readonly loadSettingsByComponentDesignGuid: { [componentDesignGuid: string]: LoadSettings };
  readonly analysisSettingsByComponentDesignGuid: { [componentDesignGuid: string]: AnalysisSettings };
}

export interface ProjectSnapshot {
  readonly guid: string;
  readonly project: Project;
  readonly layout: ProjectLayout | null;
  readonly carriedComponentConnections: readonly JackGirderConnection[];
}

export interface ProjectFileMetadata {
  readonly guid: string;
  readonly projectGuid: string;
  readonly fileName: string;
  readonly added: string;
  readonly addedByEmail: string;
  readonly byteCount: number;
}

export type LoggedUsage =
  'TddDownload' | 'JointQCDownload' | 'DigitalQCDownload' | 'ShopDrawingDownload' | 'TrussCreate' | 'ProjectCreate';

export type UsageCheck = 'TrussDesigns';

export interface UsageData {
  readonly usageCheck: UsageCheck;
  readonly quotaPerMonth: number | null;
  readonly allowedToExceed: boolean;
  readonly usageThisMonth: number;
}

export interface AssemblyGroup {
  readonly guid: string;
  readonly name: string;
  readonly organization: string;
  readonly created: string;
  readonly componentDesigns: { [guid: string]: StationComponentDesign };
  readonly notes: string | null;
  readonly archived: boolean;
}

export interface ExtractedPageScale {
  readonly pageNumber: number;
  readonly scales: readonly number[];
}

export interface OrganizationPreset<T> {
  readonly guid: string;
  readonly name: string;
  readonly organization: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly settings: T;
  readonly lumberListGuid: string | null;
  readonly plateListGuid: string | null;
  readonly hangerListGuid: string | null;
}

export interface CreatePresetRequest<T> {
  readonly presetName: string;
  readonly settings: T;
  readonly materialSelection?: MaterialSelection;
}

export interface UpdatePresetRequest<T> {
  readonly presetName: string;
  readonly settings: T;
  readonly materialSelection?: MaterialSelection;
}

export interface ProfileDefaults {
  readonly overhangCutType: OverhangCutType;
  readonly overhangDistance: number;

  readonly topChordSlope: number;
  readonly scissorBottomChordSlope: number;

  readonly heelMethod: HeelMethod;
  readonly heelHeight: number;
  readonly buttCut: number;
  readonly chordTypeRunThrough: ChordType;
  readonly interiorButtCut: number;

  readonly bearingWidth: number;

  readonly gableStudSpacing: number;
  readonly gableStuddingDirection: GableStuddingDirection;
  readonly gableMinimumStudLength: number;

  readonly atticMiddle: number;
  readonly atticWidth: number;
  readonly atticFloorDepth: number;
  readonly atticUseSingleFloorChord: boolean;
  readonly atticMaxNumberOfWebsInARun: number;

  readonly atticChaseType: ChaseType;
  readonly atticChaseMaxWidth: number;
  readonly atticChaseMinWidth: number;
  readonly atticChaseLocationType: ChaseLocationType;
  readonly atticChaseBaseSide: ComponentSide;
  readonly atticChaseLocation: number;

  readonly atticWebbingType: WebbingType;
  readonly atticLeftWebbingStart: WebbingStart;
  readonly atticRightWebbingStart: WebbingStart;
  readonly atticHorizontalWebLength: number;

  readonly atticHeight: number;

  readonly piggybackPurlinThickness: number;
  readonly piggybackRunEndVerticalsThrough: boolean;
}
